/**
 * /publisher — the Publisher dashboard.
 *
 * The Publisher is still release-centric — releases are the records this product owns — but the
 * home screen now answers the operational questions a catalogue view alone does not: what has
 * already been delivered, what still needs work, what needs attention, and what happened most
 * recently.
 *
 * Reached only with the Publisher plan. The route gate renders PublisherLockedState instead of this
 * component for an account without it, so there is no entitlement check here — one rule, one place.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { BarChart3, ChevronDown, CreditCard, Lock, Plus, Radio, Trophy } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { publisherApi } from '../api/publisherApi';
import { toApiErrorShape, UNKNOWN_API_ERROR_MESSAGE, type ApiErrorShape } from '../api/errors';
import { JVZOO_PUBLISHER_CREDIT_URLS, USE_MOCK_API } from '../config';
import { RadioCard, RadioCardGroup, Select, TextField } from '../components/forms';
import {
  PublisherActivityDrawer,
  PublisherFinancialExplainerDrawer,
  PublisherGeographyMapCard,
  PublisherOutletGlyphs,
  ReleaseRow,
} from '../components/publisher';
import { Btn, LoadError, LoadSkeleton, PrototypeNotice, SectionHeading, TabBar } from '../components/ui';
import { PUBLISHER_CREDIT_PACKS } from '../data/publisherCredits';
import { usePublisher, useSongs } from '../hooks';
import { openExternalUrl } from '../lib/externalNavigation';
import {
  isPublisherAnalyticsAllReleasesId,
  PUBLISHER_ANALYTICS_ALL_RELEASES_ID,
  PUBLISHER_ANALYTICS_RANGE_ITEMS,
  PUBLISHER_ANALYTICS_TAB_ITEMS,
  publisherAnalyticsCustomRangeReady,
} from '../lib/publisherAnalytics';
import {
  deliverySummaryLabel,
  filterPublisherActivityByRange,
  filterPublisherActivityByType,
  groupPublisherActivity,
  type PublisherDashboardActivityFilter,
  type PublisherDashboardActivityItem,
  type PublisherDashboardActivityRange,
  type PublisherDashboardBucket,
} from '../lib/publisherDashboard';
import { formatTimestamp } from '../lib/publisherFormat';
import { isFirstLoad } from '../stores/loadState';
import {
  publisherCreditPack,
  publisherCreditsLeft,
  usePublisherCreditsStore,
} from '../stores/publisherCreditsStore';
import { usePublisherModeStore } from '../stores/publisherModeStore';
import { useAuthStore } from '../stores/authStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import type {
  CreditBalance,
  PublisherAnalyticsAvailability,
  PublisherAnalyticsCountryRow,
  PublisherAnalyticsInsight,
  PublisherAnalyticsMetricCard,
  PublisherAnalyticsPayload,
  PublisherAnalyticsRangePreset,
  PublisherAnalyticsTab,
  PublisherDistributionPreferenceValues,
  PublisherFinancialBreakdownEmphasis,
  PublisherFinancialPayload,
  PublisherFinancialPayoutStatus,
  PublisherPayoutMethod,
  PublisherRelease,
  PublisherReleasePlatform,
  PublisherSettingsPayload,
  PublisherSettingsPayoutMethodFields,
  UpdatePublisherSettingsInput,
} from '../types/domain';
import { paths } from './paths';

const panelStyle = {
  border: `1px solid ${c.border}`,
  background: c.surface1,
  borderRadius: 12,
  padding: 16,
} as const;

const mutedEmptyStyle = {
  ...panelStyle,
  color: c.text2,
  fontSize: 12,
  lineHeight: 1.55,
} as const;

type PublisherSectionKey = 'overview' | 'activity' | 'analytics' | 'financial' | 'profile' | 'settings';

type OverviewKey = 'publishedSongs' | 'liveReleases' | 'inProgressReleases' | 'needsAttention' | 'drafts';

type PublisherAnalyticsLoad =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: PublisherAnalyticsPayload | null; error: null }
  | { status: 'ready'; data: PublisherAnalyticsPayload; error: null }
  | { status: 'error'; data: PublisherAnalyticsPayload | null; error: ApiErrorShape };

type PublisherFinancialLoad =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: PublisherFinancialPayload | null; error: null }
  | { status: 'ready'; data: PublisherFinancialPayload; error: null }
  | { status: 'error'; data: PublisherFinancialPayload | null; error: ApiErrorShape };

type PublisherSettingsLoad =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: PublisherSettingsPayload | null; error: null }
  | { status: 'ready'; data: PublisherSettingsPayload; error: null }
  | { status: 'error'; data: PublisherSettingsPayload | null; error: ApiErrorShape };

type PublisherSettingsSave =
  | { status: 'idle'; error: null }
  | { status: 'saving'; error: null }
  | { status: 'success'; error: null }
  | { status: 'error'; error: string };

const PUBLISHER_SECTION_ITEMS: ReadonlyArray<{ key: PublisherSectionKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'financial', label: 'Financial' },
  { key: 'activity', label: 'Activity' },
  { key: 'profile', label: 'Profile' },
  { key: 'settings', label: 'Settings' },
];

const OVERVIEW_ITEMS: ReadonlyArray<{ key: OverviewKey; label: string }> = [
  { key: 'publishedSongs', label: 'Published Songs' },
  { key: 'liveReleases', label: 'Live Releases' },
  { key: 'inProgressReleases', label: 'In Progress' },
  { key: 'needsAttention', label: 'Needs Attention' },
  { key: 'drafts', label: 'Drafts' },
];

const ACTIVITY_FILTER_ITEMS: ReadonlyArray<{ key: PublisherDashboardActivityFilter; label: string }> = [
  { key: 'all', label: 'All Events' },
  { key: 'milestones', label: 'Milestones' },
  { key: 'platform_updates', label: 'Platform Updates' },
  { key: 'earnings', label: 'Earnings' },
  { key: 'issues', label: 'Issues' },
];

const ACTIVITY_RANGE_OPTIONS: ReadonlyArray<{ value: PublisherDashboardActivityRange; label: string }> = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

const ACTIVITY_PAGE_SIZE = 8;
const ANALYTICS_CHART_WIDTH = 760;
const ANALYTICS_CHART_HEIGHT = 280;
const ANALYTICS_CHART_MARGIN = { top: 18, right: 18, bottom: 42, left: 64 } as const;
const ANALYTICS_DATE_INPUT_STYLE: CSSProperties = {
  width: '100%',
  background: c.surface2,
  border: `1px solid ${c.border}`,
  borderRadius: 8,
  fontSize: 13,
  padding: '9px 10px',
  color: c.text1,
  boxSizing: 'border-box',
};

function publisherSectionHeaderStyle(active: boolean): CSSProperties {
  return {
    appearance: 'none',
    border: 'none',
    borderBottom: `3px solid ${active ? c.accent : 'transparent'}`,
    borderRadius: 0,
    padding: '0 0 5px',
    margin: 0,
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    width: 'auto',
    color: c.text1,
    fontFamily: 'Space Grotesk',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'uppercase',
  };
}

function overviewCardStyle(active: boolean): CSSProperties {
  return {
    border: `1px solid ${active ? c.accentBorder : c.border}`,
    borderRadius: 10,
    padding: '10px 12px',
    background: active ? c.accentSoft : c.surface1,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    color: c.text1,
    boxShadow: active ? `0 0 0 1px ${c.accentBorder} inset` : 'none',
  };
}

function bucketGrid(groups: { title: string; rows: PublisherDashboardBucket[] }[]) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: 8,
      }}
    >
      {groups.map((group) => (
        <div key={group.title} style={panelStyle}>
          <p
            style={{
              margin: '0 0 9px',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: c.text2,
              textTransform: 'uppercase',
            }}
          >
            {group.title}
          </p>
          {group.rows.length === 0 ? (
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2 }}>No releases to summarise yet.</p>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
              }}
            >
              {group.rows.map((row) => (
                <li
                  key={`${group.title}:${row.label}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 11.5,
                  }}
                >
                  <span style={{ color: c.text1, minWidth: 0 }}>{row.label}</span>
                  <span style={{ color: c.text2, fontFamily: 'IBM Plex Mono', flexShrink: 0 }}>
                    {row.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function analyticsAvailabilityColor(availability: PublisherAnalyticsAvailability): string {
  if (availability === 'reported') return c.success;
  if (availability === 'not_reported') return c.accent;
  return c.text2;
}

function analyticsAvailabilityLabel(availability: PublisherAnalyticsAvailability): string {
  if (availability === 'reported') return 'Reported';
  if (availability === 'not_reported') return 'Not reported';
  return 'Preview pending';
}

function analyticsEvidenceLabel(input: { documentedByLabelGrid: boolean; providerBacked: boolean }): string {
  if (input.providerBacked) return 'Provider-backed';
  if (input.documentedByLabelGrid) return 'Documented by LabelGrid';
  return 'Not documented by LabelGrid';
}

function financialBreakdownValueColor(emphasis: PublisherFinancialBreakdownEmphasis): string {
  if (emphasis === 'negative') return c.danger;
  if (emphasis === 'total') return c.accent;
  return c.text1;
}

function financialPayoutStatusLabel(status: PublisherFinancialPayoutStatus): string {
  if (status === 'paid') return 'Paid';
  if (status === 'pending') return 'Pending';
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'failed') return 'Failed';
  return '—';
}

function financialPayoutStatusTone(status: PublisherFinancialPayoutStatus): {
  color: string;
  background: string;
} {
  if (status === 'paid') {
    return { color: c.success, background: c.successSoft };
  }
  if (status === 'pending' || status === 'scheduled') {
    return { color: c.accent, background: c.accentSoft };
  }
  if (status === 'failed') {
    return { color: c.danger, background: c.dangerSoft };
  }
  return { color: c.text2, background: c.surface2 };
}

function parsePercent(value: string): number {
  const parsed = Number.parseFloat(value.replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function countryFlagEmoji(countryCode: string): string | null {
  const normalized = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return null;
  return String.fromCodePoint(...normalized.split('').map((char) => 127397 + char.charCodeAt(0)));
}

function compactAnalyticsDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function deleteGraceRemainingLabel(purgeAfter: string): string {
  const remainingMs = Date.parse(purgeAfter) - Date.now();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return 'Less than a minute remaining';
  const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'} remaining`;
  if (minutes === 0) return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
  return `${hours} hour${hours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'} remaining`;
}

function analyticsChartX(index: number, count: number): number {
  const plotWidth = ANALYTICS_CHART_WIDTH - ANALYTICS_CHART_MARGIN.left - ANALYTICS_CHART_MARGIN.right;
  if (count <= 1) return ANALYTICS_CHART_MARGIN.left + plotWidth / 2;
  return ANALYTICS_CHART_MARGIN.left + (index / (count - 1)) * plotWidth;
}

function analyticsChartY(value: number, maxValue: number): number {
  const plotHeight = ANALYTICS_CHART_HEIGHT - ANALYTICS_CHART_MARGIN.top - ANALYTICS_CHART_MARGIN.bottom;
  return ANALYTICS_CHART_MARGIN.top + plotHeight - (value / maxValue) * plotHeight;
}

function analyticsChartLine(values: Array<number | null>, maxValue: number): string {
  let path = '';
  let drawing = false;
  values.forEach((value, index) => {
    if (value === null) {
      drawing = false;
      return;
    }
    const command = drawing ? 'L' : 'M';
    path += `${command} ${analyticsChartX(index, values.length)} ${analyticsChartY(value, maxValue)} `;
    drawing = true;
  });
  return path.trim();
}

function analyticsChartArea(values: number[], maxValue: number): string {
  if (values.length === 0) return '';
  const baseline = ANALYTICS_CHART_HEIGHT - ANALYTICS_CHART_MARGIN.bottom;
  const line = analyticsChartLine(values, maxValue);
  const firstX = analyticsChartX(0, values.length);
  const lastX = analyticsChartX(values.length - 1, values.length);
  return `M ${firstX} ${baseline} ${line.replace(/^M/, 'L')} L ${lastX} ${baseline} Z`;
}

function niceAnalyticsMaximum(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const niceNormalized = normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
}

function compactAnalyticsCount(value: number): string {
  return Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function analyticsDateTickIndexes(count: number): number[] {
  if (count <= 1) return [0];
  const tickCount = Math.min(6, count);
  return Array.from(new Set(Array.from({ length: tickCount }, (_, index) => Math.round((index / (tickCount - 1)) * (count - 1)))));
}

function isMilestoneReached(item: PublisherDashboardActivityItem): boolean {
  return item.type === 'milestone' && item.event.message === 'Milestone reached';
}

function isGrossReported(item: PublisherDashboardActivityItem): boolean {
  return item.type === 'earning';
}

function showsOutletGlyphs(item: PublisherDashboardActivityItem): boolean {
  return item.event.message === 'Release Approved' || item.event.message === 'Release Distributed';
}

function matchesActivityOutlet(message: string, outlet: { outletKey: string; outletName: string }): boolean {
  const haystack = message.trim().toLowerCase();
  const outletName = outlet.outletName.trim().toLowerCase();
  const outletKey = outlet.outletKey.trim().toLowerCase();
  if (!haystack || (!outletName && !outletKey)) return false;
  if (outletName && haystack.includes(outletName)) return true;
  if (outletKey && haystack.includes(outletKey.replace(/-/g, ' '))) return true;
  return outletKey === 'apple-music' && haystack.includes('apple music');
}

function activityTitlePlatforms(
  item: PublisherDashboardActivityItem,
  platforms: readonly PublisherReleasePlatform[]
): PublisherReleasePlatform[] {
  if (showsOutletGlyphs(item)) return [...platforms];
  if (/\bnow live on\b/i.test(item.event.message)) {
    return platforms.filter((platform) => matchesActivityOutlet(item.event.message, platform));
  }
  return [];
}

function activityCardTitle(item: PublisherDashboardActivityItem): string {
  if (isGrossReported(item)) return 'Gross Reported';
  return item.event.message;
}

function activityButtonLabel(item: PublisherDashboardActivityItem): string {
  return `Open ${activityCardTitle(item)} activity details`;
}

function settingsDraftFromPayload(payload: PublisherSettingsPayload): UpdatePublisherSettingsInput {
  return {
    payoutMethod: payload.payoutMethod.value as PublisherPayoutMethod,
    payoutThreshold: payload.payoutThreshold.value,
    payoutDetailsByMethod: payload.payoutDetailsByMethod.reduce<UpdatePublisherSettingsInput['payoutDetailsByMethod']>(
      (acc, section) => ({
        ...acc,
        [section.method]: Object.fromEntries(section.fields.map((field) => [field.key, field.value])),
      }),
      {
        paypal: {},
        stripe: {},
        bank_transfer: {},
        wise: {},
      }
    ),
    distributionPreferences: {
      defaultGenre: payload.distributionPreferences.defaultGenre.value,
      defaultReleaseType: payload.distributionPreferences.defaultReleaseType.value,
      autoSubmitTo: payload.distributionPreferences.autoSubmitTo.value,
      contentId: payload.distributionPreferences.contentId.value,
      explicitContent: payload.distributionPreferences.explicitContent.value,
    },
  };
}

function payoutMethodFieldsFor(
  payload: PublisherSettingsPayload,
  method: PublisherPayoutMethod
): PublisherSettingsPayoutMethodFields | null {
  return payload.payoutDetailsByMethod.find((section) => section.method === method) ?? null;
}

function payoutMethodLabel(payload: PublisherSettingsPayload, method: PublisherPayoutMethod): string {
  const option = payload.payoutMethod.options.find(
    (candidate) => typeof candidate !== 'string' && candidate.value === method
  );
  return typeof option === 'string' ? option : option?.label ?? method;
}

function payoutMethodDetail(payload: PublisherSettingsPayload, method: PublisherPayoutMethod): string {
  const section = payoutMethodFieldsFor(payload, method);
  const populatedField = section?.fields.find((field) => field.value.trim());
  if (populatedField) return populatedField.value.trim();
  return `No ${payoutMethodLabel(payload, method)} payout details saved`;
}

function emptyDistributionPreferences(): PublisherDistributionPreferenceValues {
  return {
    defaultGenre: '',
    defaultReleaseType: '',
    autoSubmitTo: '',
    contentId: '',
    explicitContent: '',
  };
}

function createEmptySettingsDraft(): UpdatePublisherSettingsInput {
  return {
    payoutMethod: 'paypal',
    payoutThreshold: '',
    payoutDetailsByMethod: {
      paypal: {},
      stripe: {},
      bank_transfer: {},
      wise: {},
    },
    distributionPreferences: emptyDistributionPreferences(),
  };
}

export default function Publisher() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    releases,
    dashboard,
    pendingReleaseDeletions,
    platformRowsFor,
    setLastVisibleSurface,
    load,
    loadPublisher,
    undoReleaseDeletion,
  } = usePublisher();
  const { songs } = useSongs();
  const setPublisherDeleteTarget = useUiStore((s) => s.setPublisherDeleteTarget);
  const selectedPublisherCreditPackId = usePublisherCreditsStore((s) => s.selectedPack);
  const setSelectedPublisherCreditPack = usePublisherCreditsStore((s) => s.setSelectedPack);
  const hydratePublisherCredits = usePublisherCreditsStore((s) => s.hydrate);
  const userIsAdmin = useAuthStore((s) => s.user?.isAdmin === true);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const adminPublisherMode = usePublisherModeStore((s) => s.adminMode);
  const toggleAdminPublisherMode = usePublisherModeStore((s) => s.toggleAdminMode);
  const publisherCreditsPanelRef = useRef<HTMLElement | null>(null);
  const [activeSection, setActiveSection] = useState<PublisherSectionKey>('overview');
  const [activeOverview, setActiveOverview] = useState<OverviewKey>(OVERVIEW_ITEMS[0].key);
  const [activityFilter, setActivityFilter] = useState<PublisherDashboardActivityFilter>('all');
  const [activityRange, setActivityRange] = useState<PublisherDashboardActivityRange>('7d');
  const [visibleActivityCount, setVisibleActivityCount] = useState(ACTIVITY_PAGE_SIZE);
  const [collapsedActivityGroups, setCollapsedActivityGroups] = useState<Record<string, boolean>>({});
  const [selectedActivity, setSelectedActivity] = useState<PublisherDashboardActivityItem | null>(null);
  const [selectedAnalyticsReleaseId, setSelectedAnalyticsReleaseId] = useState('');
  const [analyticsPreset, setAnalyticsPreset] = useState<PublisherAnalyticsRangePreset>('30d');
  const [analyticsCustomStartDate, setAnalyticsCustomStartDate] = useState('2026-08-01');
  const [analyticsCustomEndDate, setAnalyticsCustomEndDate] = useState('2026-08-28');
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState<PublisherAnalyticsTab>('streams');
  const [analyticsHoveredPoint, setAnalyticsHoveredPoint] = useState<number | null>(null);
  const [analyticsLoad, setAnalyticsLoad] = useState<PublisherAnalyticsLoad>({
    status: 'idle',
    data: null,
    error: null,
  });
  const [overviewAnalyticsLoad, setOverviewAnalyticsLoad] = useState<PublisherAnalyticsLoad>({
    status: 'idle',
    data: null,
    error: null,
  });
  const [financialLoad, setFinancialLoad] = useState<PublisherFinancialLoad>({
    status: 'idle',
    data: null,
    error: null,
  });
  const [financialSectionsCollapsed, setFinancialSectionsCollapsed] = useState({
    platforms: true,
    paymentHistory: true,
  });
  const [settingsLoad, setSettingsLoad] = useState<PublisherSettingsLoad>({
    status: 'idle',
    data: null,
    error: null,
  });
  const [settingsSave, setSettingsSave] = useState<PublisherSettingsSave>({ status: 'idle', error: null });
  const [settingsDraft, setSettingsDraft] = useState<UpdatePublisherSettingsInput>(createEmptySettingsDraft);
  const [settingsSectionsCollapsed, setSettingsSectionsCollapsed] = useState({
    payout: false,
    distribution: true,
    tax: true,
  });
  const [overviewCatalogueCollapsed, setOverviewCatalogueCollapsed] = useState(true);
  const [publisherCreditsExpanded, setPublisherCreditsExpanded] = useState(false);
  const [publisherCreditsLoad, setPublisherCreditsLoad] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    data: CreditBalance | null;
  }>({ status: 'idle', data: null });
  const requestedSection = searchParams.get('section');
  const [openFinancialExplainer, setOpenFinancialExplainer] = useState<
    'breakdown' | 'education' | null
  >(null);
  const effectivePublisherMode = userIsAdmin && adminPublisherMode === 'mock' ? 'mock' : 'real';

  useEffect(() => {
    setLastVisibleSurface({ kind: 'dashboard' });
  }, [setLastVisibleSurface]);

  useEffect(() => {
    if (requestedSection === 'settings') {
      setActiveSection('settings');
      return;
    }
    if (requestedSection === 'financial') {
      setActiveSection('financial');
    }
  }, [requestedSection]);

  useEffect(() => {
    setVisibleActivityCount(ACTIVITY_PAGE_SIZE);
  }, [activityFilter, activityRange]);

  useEffect(() => {
    if (activeSection !== 'profile') {
      setPublisherCreditsExpanded(false);
    }
  }, [activeSection]);

  useEffect(() => {
    setPublisherCreditsLoad({ status: 'idle', data: null });
  }, [userId]);

  useEffect(() => {
    if (activeSection !== 'profile') return;
    if (USE_MOCK_API) {
      const localBalance = usePublisherCreditsStore.getState();
      setPublisherCreditsLoad({
        status: 'ready',
        data: {
          total: localBalance.total,
          used: localBalance.used,
          reserved: localBalance.reserved ?? 0,
          unlimited: localBalance.unlimited,
        },
      });
      return;
    }

    let live = true;
    setPublisherCreditsLoad({ status: 'loading', data: null });
    void publisherApi
      .getPublisherCredits()
      .then((balance) => {
        if (!live) return;
        hydratePublisherCredits(balance);
        setPublisherCreditsLoad({ status: 'ready', data: balance });
      })
      .catch(() => {
        if (!live) return;
        setPublisherCreditsLoad({ status: 'error', data: null });
      });
    return () => {
      live = false;
    };
  }, [activeSection, hydratePublisherCredits, userId]);

  useEffect(() => {
    if (!publisherCreditsExpanded) return undefined;

    function handlePointerDown(event: PointerEvent) {
      const panel = publisherCreditsPanelRef.current;
      const target = event.target;
      if (!panel || !(target instanceof Node) || panel.contains(target)) return;
      setPublisherCreditsExpanded(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [publisherCreditsExpanded]);

  useEffect(() => {
    setAnalyticsLoad({ status: 'idle', data: null, error: null });
    setOverviewAnalyticsLoad({ status: 'idle', data: null, error: null });
    setFinancialLoad({ status: 'idle', data: null, error: null });

    if (USE_MOCK_API && effectivePublisherMode === 'real') return;
    void loadPublisher(effectivePublisherMode);
  }, [effectivePublisherMode, loadPublisher, userIsAdmin]);

  const firstLoad = isFirstLoad(load, releases.length > 0);
  const selectedPublisherCreditPack = publisherCreditPack(selectedPublisherCreditPackId);
  const activeFinancialExplainer =
    financialLoad.status !== 'ready'
      ? null
      : openFinancialExplainer === 'breakdown'
        ? financialLoad.data.breakdown.explainer ?? null
        : openFinancialExplainer === 'education'
          ? financialLoad.data.education.explainer ?? null
          : null;
  const draftReleases = dashboard.resumeReleases.filter((release) => release.status === 'draft');
  const inProgressReleases = dashboard.resumeReleases.filter(
    (release) => release.status === 'in_review' || release.status === 'processing'
  );

  const rangeFilteredActivity = useMemo(
    () => filterPublisherActivityByRange(dashboard.activity, activityRange),
    [activityRange, dashboard.activity]
  );
  const filteredActivity = useMemo(
    () => filterPublisherActivityByType(rangeFilteredActivity, activityFilter),
    [activityFilter, rangeFilteredActivity]
  );
  const visibleActivity = useMemo(
    () => filteredActivity.slice(0, visibleActivityCount),
    [filteredActivity, visibleActivityCount]
  );
  const groupedActivity = useMemo(
    () => groupPublisherActivity(visibleActivity),
    [visibleActivity]
  );

  useEffect(() => {
    setCollapsedActivityGroups((current) => {
      if (groupedActivity.length === 0) return {};
      const next: Record<string, boolean> = {};
      groupedActivity.forEach((group, index) => {
        next[group.label] = current[group.label] ?? index !== 0;
      });
      return next;
    });
  }, [groupedActivity]);

  const analyticsReleaseOptions = useMemo(
    () => [
      { value: PUBLISHER_ANALYTICS_ALL_RELEASES_ID, label: 'All' },
      ...dashboard.allReleases.map((release) => ({
        value: release.id,
        label: `${release.title.trim() || 'Untitled release'} · ${release.artistName.trim() || 'Unknown artist'}`,
      })),
    ],
    [dashboard.allReleases]
  );

  const selectedAnalyticsRelease = useMemo(
    () => dashboard.allReleases.find((release) => release.id === selectedAnalyticsReleaseId) ?? null,
    [dashboard.allReleases, selectedAnalyticsReleaseId]
  );
  const analyticsShowsAllReleases = isPublisherAnalyticsAllReleasesId(selectedAnalyticsReleaseId);

  const customAnalyticsRangeReady = publisherAnalyticsCustomRangeReady(
    analyticsCustomStartDate,
    analyticsCustomEndDate
  );

  useEffect(() => {
    const preferredReleaseId = dashboard.allReleases.length > 0 ? PUBLISHER_ANALYTICS_ALL_RELEASES_ID : '';
    setSelectedAnalyticsReleaseId((current) => {
      if (
        current &&
        (isPublisherAnalyticsAllReleasesId(current) || dashboard.allReleases.some((release) => release.id === current))
      ) {
        return current;
      }
      return preferredReleaseId;
    });
  }, [dashboard.allReleases]);

  useEffect(() => {
    if (activeSection !== 'analytics') return;
    if (!selectedAnalyticsReleaseId) {
      setAnalyticsLoad({ status: 'idle', data: null, error: null });
      return;
    }
    if (analyticsPreset === 'custom' && !customAnalyticsRangeReady) {
      setAnalyticsLoad((prev) =>
        prev.data &&
        prev.data.releaseId === selectedAnalyticsReleaseId &&
        prev.data.selectedRange.preset === 'custom' &&
        prev.data.selectedRange.startDate === analyticsCustomStartDate &&
        prev.data.selectedRange.endDate === analyticsCustomEndDate
          ? prev
          : { status: 'idle', data: null, error: null }
      );
      return;
    }

    let live = true;
    setAnalyticsLoad({ status: 'loading', data: null, error: null });
    void publisherApi
      .getPublisherAnalytics({
        releaseId: selectedAnalyticsReleaseId,
        preset: analyticsPreset,
        startDate: analyticsPreset === 'custom' ? analyticsCustomStartDate : undefined,
        endDate: analyticsPreset === 'custom' ? analyticsCustomEndDate : undefined,
        mode: effectivePublisherMode,
      })
      .then((payload) => {
        if (!live) return;
        setAnalyticsLoad({ status: 'ready', data: payload, error: null });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setAnalyticsLoad({
          status: 'error',
          data: null,
          error: toApiErrorShape(error),
        });
      });

    return () => {
      live = false;
    };
  }, [
    activeSection,
    selectedAnalyticsReleaseId,
    analyticsPreset,
    analyticsCustomStartDate,
    analyticsCustomEndDate,
    customAnalyticsRangeReady,
    effectivePublisherMode,
  ]);

  useEffect(() => {
    if (activeSection !== 'overview' || overviewAnalyticsLoad.status !== 'idle') return;

    setOverviewAnalyticsLoad({ status: 'loading', data: null, error: null });
    void publisherApi
      .getPublisherAnalytics({
        releaseId: PUBLISHER_ANALYTICS_ALL_RELEASES_ID,
        preset: 'all',
        mode: effectivePublisherMode,
      })
      .then((payload) => {
        setOverviewAnalyticsLoad({ status: 'ready', data: payload, error: null });
      })
      .catch((error: unknown) => {
        setOverviewAnalyticsLoad({
          status: 'error',
          data: null,
          error: toApiErrorShape(error),
        });
      });
  }, [activeSection, effectivePublisherMode, overviewAnalyticsLoad.status]);

  useEffect(() => {
    if ((activeSection !== 'overview' && activeSection !== 'financial') || financialLoad.status !== 'idle') return;

    setFinancialLoad({ status: 'loading', data: null, error: null });
    void publisherApi
      .getPublisherFinancial({ mode: effectivePublisherMode })
      .then((payload) => {
        setFinancialLoad({ status: 'ready', data: payload, error: null });
      })
      .catch((error: unknown) => {
        setFinancialLoad({
          status: 'error',
          data: null,
          error: toApiErrorShape(error),
        });
      });
  }, [activeSection, effectivePublisherMode, financialLoad.status]);

  useEffect(() => {
    if ((activeSection !== 'settings' && (activeSection !== 'financial' || USE_MOCK_API)) || settingsLoad.status !== 'idle') return;

    setSettingsLoad({ status: 'loading', data: null, error: null });
    void publisherApi
      .getPublisherSettings()
      .then((payload) => {
        setSettingsLoad({ status: 'ready', data: payload, error: null });
      })
      .catch((error: unknown) => {
        setSettingsLoad({
          status: 'error',
          data: null,
          error: toApiErrorShape(error),
        });
      });
  }, [activeSection, settingsLoad.status]);

  useEffect(() => {
    if (settingsLoad.status !== 'ready') return;
    setSettingsDraft(settingsDraftFromPayload(settingsLoad.data));
  }, [settingsLoad]);

  const activeSettingsPayoutFields =
    settingsLoad.status === 'ready' ? payoutMethodFieldsFor(settingsLoad.data, settingsDraft.payoutMethod) : null;
  const loadedSettingsDraft = settingsLoad.status === 'ready' ? settingsDraftFromPayload(settingsLoad.data) : null;
  const settingsDirty = loadedSettingsDraft ? JSON.stringify(settingsDraft) !== JSON.stringify(loadedSettingsDraft) : false;
  const savingSettings = settingsSave.status === 'saving';
  const settingsStatusLabel =
    settingsLoad.status !== 'ready'
      ? null
      : settingsLoad.data.source === 'saved'
        ? settingsLoad.data.updatedAt
          ? `Saved · ${formatTimestamp(settingsLoad.data.updatedAt)}`
          : 'Saved'
        : 'Default';

  function updateSettingsDistributionPreference(
    key: keyof PublisherDistributionPreferenceValues,
    value: string
  ) {
    setSettingsDraft((current) => ({
      ...current,
      distributionPreferences: {
        ...current.distributionPreferences,
        [key]: value,
      },
    }));
    setSettingsSave((current) => (current.status === 'error' || current.status === 'success' ? { status: 'idle', error: null } : current));
  }

  function updateSettingsPayoutMethod(value: string) {
    setSettingsDraft((current) => ({
      ...current,
      payoutMethod: value as PublisherPayoutMethod,
    }));
    setSettingsSave((current) => (current.status === 'error' || current.status === 'success' ? { status: 'idle', error: null } : current));
  }

  function updateSettingsPayoutThreshold(value: string) {
    setSettingsDraft((current) => ({
      ...current,
      payoutThreshold: value,
    }));
    setSettingsSave((current) => (current.status === 'error' || current.status === 'success' ? { status: 'idle', error: null } : current));
  }

  function updateSettingsPayoutField(method: PublisherPayoutMethod, key: string, value: string) {
    setSettingsDraft((current) => ({
      ...current,
      payoutDetailsByMethod: {
        ...current.payoutDetailsByMethod,
        [method]: {
          ...current.payoutDetailsByMethod[method],
          [key]: value,
        },
      },
    }));
    setSettingsSave((current) => (current.status === 'error' || current.status === 'success' ? { status: 'idle', error: null } : current));
  }

  function syncFinancialPayoutSummary(payload: PublisherSettingsPayload) {
    setFinancialLoad((current) => {
      if (current.status !== 'ready') return current;
      const method = payload.payoutMethod.value as PublisherPayoutMethod;
      return {
        ...current,
        data: {
          ...current.data,
          payoutMethod: {
            ...current.data.payoutMethod,
            method: payoutMethodLabel(payload, method),
            detail: payoutMethodDetail(payload, method),
          },
        },
      };
    });
  }

  async function saveSettings() {
    if (settingsLoad.status !== 'ready' || !settingsDirty || savingSettings) return;
    setSettingsSave({ status: 'saving', error: null });
    try {
      const payload = await publisherApi.updatePublisherSettings(settingsDraft);
      setSettingsLoad({ status: 'ready', data: payload, error: null });
      syncFinancialPayoutSummary(payload);
      setSettingsSave({ status: 'success', error: null });
    } catch (error: unknown) {
      setSettingsSave({
        status: 'error',
        error: error instanceof Error ? error.message : UNKNOWN_API_ERROR_MESSAGE,
      });
    }
  }

  function renderSectionDescription(text: string) {
    return (
      <PrototypeNotice
        style={{
          marginBottom: 10,
          border: `1px solid ${c.accentBorder}`,
          background: c.accentSoft,
          color: c.accent,
        }}
      >
        {text}
      </PrototypeNotice>
    );
  }

  function isMissingRealPublisherRoute(error: ApiErrorShape | null): boolean {
    return effectivePublisherMode === 'real' && error?.kind === 'notFound';
  }

  function renderMissingPublisherRouteNotice() {
    return <PrototypeNotice>Nothing to see here at the moment</PrototypeNotice>;
  }

  function openPublisherCreditCheckout(): void {
    openExternalUrl(publisherCreditCheckoutUrl);
  }

  const publisherCreditCheckoutUrl =
    JVZOO_PUBLISHER_CREDIT_URLS[selectedPublisherCreditPack.id] ??
    JVZOO_PUBLISHER_CREDIT_URLS.pub10;

  function renderPublisherCreditsPanel() {
    const publisherCreditsAvailable = publisherCreditsLoad.data
      ? publisherCreditsLeft(publisherCreditsLoad.data)
      : null;
    const publisherCreditsLabel =
      publisherCreditsLoad.status === 'loading' || publisherCreditsLoad.status === 'idle'
        ? 'Loading balance…'
        : publisherCreditsLoad.status === 'error'
          ? 'Balance unavailable'
          : publisherCreditsLoad.data?.unlimited
            ? 'Unlimited Publisher credits'
            : `${publisherCreditsAvailable?.toLocaleString() ?? 0} Publisher credits available`;

    return (
      <section ref={publisherCreditsPanelRef} style={{ ...panelStyle, display: 'grid', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <CreditCard size={17} color={c.accent} style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                Publisher credits
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                Add publishing capacity whenever you need it.
              </p>
              <p
                aria-live="polite"
                style={{ margin: '5px 0 0', fontSize: 12, color: c.accent, fontWeight: 600 }}
              >
                {publisherCreditsLabel}
              </p>
            </div>
          </div>
        </div>

        {publisherCreditsExpanded ? (
          <>
            <RadioCardGroup label="Publisher credit pack" gap={6}>
              {PUBLISHER_CREDIT_PACKS.map((pack) => (
                <RadioCard
                  key={pack.id}
                  name="publisher-credit-pack"
                  value={pack.id}
                  checked={selectedPublisherCreditPack.id === pack.id}
                  onSelect={setSelectedPublisherCreditPack}
                  ariaLabel={`${pack.credits} Publisher credits for $${pack.price}`}
                  size="compact"
                  trailing={
                    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'IBM Plex Mono', color: c.text1 }}>
                      ${pack.price}
                    </span>
                  }
                >
                  <span style={{ display: 'block' }}>
                    <span style={{ fontWeight: 600, fontSize: 12.5, display: 'block', color: c.text1 }}>
                      {pack.credits} Publisher credits
                      {pack.best ? (
                        <span
                          style={{
                            background: c.accentSoft,
                            color: c.accent,
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 4,
                            marginLeft: 6,
                          }}
                        >
                          Best value
                        </span>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 11, color: c.text3, fontFamily: 'IBM Plex Mono', display: 'block' }}>
                      ${pack.per} / credit
                    </span>
                  </span>
                </RadioCard>
              ))}
            </RadioCardGroup>

            <Btn
              href={publisherCreditCheckoutUrl}
              pendingOnNavigate
              onClick={openPublisherCreditCheckout}
            >
              <Lock size={13} />
              {`Top up Publisher credits — ${selectedPublisherCreditPack.credits} for $${selectedPublisherCreditPack.price}`}
            </Btn>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setPublisherCreditsExpanded(true)}>
              <Plus size={13} />
              Top up credits
            </Btn>
          </div>
        )}
      </section>
    );
  }

  function renderReleaseSection(options: {
    title: string;
    subtitle: string;
    releases: PublisherRelease[];
    emptyMessage: string;
    showDeliverySummary?: boolean;
    showFailureReason?: boolean;
    showHeading?: boolean;
  }) {
    return (
      <section style={{ marginBottom: 8 }}>
        {options.showHeading === false ? null : (
          <SectionHeading
            as="h2"
            size="sub"
            title={options.title}
            subtitle={options.subtitle}
            marginBottom={10}
          />
        )}
        {options.releases.length === 0 ? (
          <div style={mutedEmptyStyle}>{options.emptyMessage}</div>
        ) : (
          <div className="lyx-release-list">
            {options.releases.map((release) =>
              options.showFailureReason ? (
                <div key={release.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <ReleaseRow
                    release={release}
                    songs={songs}
                    onOpen={(id) => navigate(paths.publisherRelease(id))}
                    onDelete={(release) => setPublisherDeleteTarget(release, 'dashboard')}
                  />
                  <div
                    style={{
                      border: `1px solid ${c.danger}`,
                      background: c.dangerSoft,
                      borderRadius: 10,
                      padding: '10px 12px',
                    }}
                  >
                    <p style={{ margin: '0 0 4px', fontSize: 11.5, fontWeight: 600, color: c.text1 }}>
                      Recorded issue
                    </p>
                    <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: c.text2 }}>
                      {release.failureReason ??
                        'This release failed, but no specific reason was recorded on the release yet.'}
                    </p>
                  </div>
                </div>
              ) : (
                <ReleaseRow
                  key={release.id}
                  release={release}
                  songs={songs}
                  deliverySummary={
                    options.showDeliverySummary ? deliverySummaryLabel(platformRowsFor(release.id)) : null
                  }
                  onOpen={(id) => navigate(paths.publisherRelease(id))}
                  onDelete={(release) => setPublisherDeleteTarget(release, 'dashboard')}
                />
              )
            )}
          </div>
        )}
      </section>
    );
  }

  function renderActivitySection(subtitle: string, activity: PublisherDashboardActivityItem[]) {
    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription(subtitle)}

        <div className="lyx-publisher-activity-filters">
          <div className="lyx-publisher-activity-filter-control">
            <Select
              label="Filter by"
              value={activityFilter}
              onChange={(value) => setActivityFilter(value as PublisherDashboardActivityFilter)}
              options={ACTIVITY_FILTER_ITEMS.map((item) => ({ value: item.key, label: item.label }))}
              style={{ minWidth: 170 }}
            />
          </div>
          <div className="lyx-publisher-activity-range">
            <Select
              label="Date Range"
              ariaLabel="Activity date range"
              value={activityRange}
              onChange={(value) => setActivityRange(value as PublisherDashboardActivityRange)}
              options={ACTIVITY_RANGE_OPTIONS}
              style={{ minWidth: 170 }}
            />
          </div>
        </div>

        {activity.length === 0 ? (
          <div style={mutedEmptyStyle}>No release activity has been recorded for this account yet.</div>
        ) : filteredActivity.length === 0 ? (
          <div style={mutedEmptyStyle}>No activity matches the current filters.</div>
        ) : (
          <div className="lyx-publisher-activity-feed">
            {groupedActivity.map((group) => {
              const collapsed = collapsedActivityGroups[group.label] ?? false;
              return (
                <section key={group.label} className="lyx-publisher-activity-group" aria-label={group.label}>
                  <button
                    type="button"
                    className="lyx-publisher-activity-group-label"
                    aria-expanded={!collapsed}
                    onClick={() =>
                      setCollapsedActivityGroups((current) => ({
                        ...current,
                        [group.label]: !(current[group.label] ?? false),
                      }))
                    }
                    style={{
                      appearance: 'none',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      width: '100%',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span>{group.label}</span>
                    <ChevronDown
                      aria-hidden="true"
                      size={14}
                      color={c.text2}
                      style={{
                        flexShrink: 0,
                        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 160ms ease',
                      }}
                    />
                  </button>
                  {!collapsed ? (
                    <ul
                      style={{
                        listStyle: 'none',
                        margin: 0,
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      {group.items.map((item) => {
                        const { event, release } = item;
                        const relatedPlatforms = release ? platformRowsFor(release.id) : [];
                        const titlePlatforms = activityTitlePlatforms(item, relatedPlatforms);
                        return (
                          <li key={event.id}>
                            <button
                              type="button"
                              className="lyx-event-row"
                              aria-label={activityButtonLabel(item)}
                              onClick={() => setSelectedActivity(item)}
                              style={{
                                ...panelStyle,
                                appearance: 'none',
                                width: '100%',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  marginTop: 5,
                                  flexShrink: 0,
                                  background: event.source === 'provider' ? c.accentFill : c.borderStrong,
                                }}
                              />
                              <div className="lyx-event-text">
                                <p style={{ margin: '0 0 3px', fontSize: 12, color: c.text1, lineHeight: 1.5 }}>
                                  {isMilestoneReached(item) ? (
                                    <span
                                      style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        width: '100%',
                                      }}
                                    >
                                      <span style={{ minWidth: 0 }}>Milestone reached</span>
                                      <Trophy aria-hidden="true" size={13} color={c.accent} style={{ flexShrink: 0, marginTop: 1 }} />
                                    </span>
                                  ) : isGrossReported(item) ? (
                                    <span
                                      style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        width: '100%',
                                      }}
                                    >
                                      <span style={{ minWidth: 0 }}>Gross Reported</span>
                                      <BarChart3 aria-hidden="true" size={13} color={c.accent} style={{ flexShrink: 0, marginTop: 1 }} />
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        width: '100%',
                                      }}
                                    >
                                      <span style={{ minWidth: 0 }}>{activityCardTitle(item)}</span>
                                      {titlePlatforms.length > 0 ? (
                                        <PublisherOutletGlyphs
                                          platforms={titlePlatforms}
                                          size={14}
                                          ariaLabel={`Reported outlets for ${activityCardTitle(item)}: ${titlePlatforms.map((platform) => platform.outletName).join(', ')}`}
                                        />
                                      ) : null}
                                    </span>
                                  )}
                                </p>
                                {release ? (
                                  <p style={{ margin: '0 0 3px', fontSize: 11, color: c.text2, lineHeight: 1.5 }}>
                                    {release.title.trim() || 'Untitled release'} · {release.releaseType}
                                  </p>
                                ) : null}
                                <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>
                                  {formatTimestamp(event.at)}
                                  {event.source === 'provider' ? ' · reported by the distributor' : ''}
                                </p>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}

        {filteredActivity.length > visibleActivityCount ? (
          <div className="lyx-publisher-activity-more">
            <Btn variant="secondary" onClick={() => setVisibleActivityCount((count) => count + ACTIVITY_PAGE_SIZE)}>
              Load More
            </Btn>
          </div>
        ) : null}
      </section>
    );
  }

  function renderPlaceholderSection(subtitle: string, message: string) {
    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription(subtitle)}
        <div style={mutedEmptyStyle}>{message}</div>
      </section>
    );
  }

  function renderSnapshotSection() {
    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription('Compact summaries of what the current catalogue looks like across all release states in this account.')}
        {bucketGrid([
          { title: 'Release Types', rows: dashboard.releaseTypes },
          { title: 'Genres', rows: dashboard.genres },
          { title: 'Languages', rows: dashboard.languages },
          { title: 'Explicitness', rows: dashboard.explicitness },
        ])}
      </section>
    );
  }

  function renderFinancialSummaryRow(summary: PublisherFinancialPayload['summary']) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {summary.map((item) => (
          <div key={item.key} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: c.text2,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              {item.label}
            </p>
            <p
              style={{
                margin: 0,
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 24,
                color: c.text1,
              }}
            >
              {item.value}
            </p>
            {item.note ? <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{item.note}</p> : null}
          </div>
        ))}
      </div>
    );
  }

  function renderAnalyticsKeyMetricsGrid(cards: readonly PublisherAnalyticsMetricCard[]) {
    return (
      <div
        className={cards.length === 4 ? 'lyx-publisher-analytics-key-metrics lyx-publisher-analytics-key-metrics--quad' : 'lyx-publisher-analytics-key-metrics'}
        style={{
          display: 'grid',
          gap: 8,
        }}
      >
        {cards.map((card) => renderAnalyticsValueCard(card))}
      </div>
    );
  }

  function analyticsMetricBreakdownValueColor(availability: PublisherAnalyticsAvailability): string {
    if (availability === 'reported') return c.text1;
    if (availability === 'not_reported') return c.accent;
    return c.text2;
  }

  function renderAnalyticsMetricBreakdown(card: PublisherAnalyticsMetricCard) {
    if (!card.breakdown || card.breakdown.length === 0) return null;

    return (
      <div role="list" aria-label={`${card.label} by platform`} style={{ display: 'grid', gap: 6, marginTop: 2 }}>
        {card.breakdown.map((item, index) => (
          <div
            key={`${card.key}:${item.outletKey}`}
            role="listitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              minWidth: 0,
              paddingTop: index === 0 ? 6 : 0,
              borderTop: `1px solid ${c.border}`,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <PublisherOutletGlyphs
                platforms={[
                  {
                    releaseId: `analytics-${card.key}`,
                    outletKey: item.outletKey,
                    outletName: item.outletName,
                    status: '',
                    updatedAt: null,
                  },
                ]}
                size={14}
                ariaLabel={item.outletName}
              />
              <span
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 11.5,
                  color: c.text2,
                }}
              >
                {item.outletName}
              </span>
            </span>
            <span
              style={{
                flexShrink: 0,
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 13,
                color: analyticsMetricBreakdownValueColor(item.availability),
              }}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  function renderAnalyticsValueCard(input: PublisherAnalyticsMetricCard) {
    const tone = analyticsAvailabilityColor(input.availability);
    const showsBreakdown = (input.breakdown?.length ?? 0) > 0;
    return (
      <article
        key={input.label}
        style={{
          border: `1px solid ${input.availability === 'reported' ? c.border : c.accentBorder}`,
          borderRadius: 10,
          padding: '12px 14px',
          background: input.availability === 'reported' ? c.surface1 : c.surface2,
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 11, color: c.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            {input.label}
          </p>
          <span style={{ fontSize: 11, color: tone, fontWeight: 600 }}>{analyticsAvailabilityLabel(input.availability)}</span>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 22,
            color: input.availability === 'unavailable' ? c.text2 : c.text1,
          }}
        >
          {input.value}
        </p>
        {showsBreakdown ? (
          renderAnalyticsMetricBreakdown(input)
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
              {analyticsEvidenceLabel(input)}
            </p>
            {input.note ? <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{input.note}</p> : null}
          </>
        )}
      </article>
    );
  }

  function renderOverviewTopCountriesCard(countries: readonly PublisherAnalyticsCountryRow[]) {
    const rankedCountries = [...countries]
      .sort((left, right) => parsePercent(right.share) - parsePercent(left.share))
      .slice(0, 5);
    const availability = rankedCountries.some((country) => country.availability === 'reported')
      ? 'reported'
      : rankedCountries.some((country) => country.availability === 'not_reported')
        ? 'not_reported'
        : 'unavailable';
    const tone = analyticsAvailabilityColor(availability);

    return (
      <article
        style={{
          border: `1px solid ${availability === 'reported' ? c.border : c.accentBorder}`,
          borderRadius: 10,
          padding: '12px 14px',
          background: availability === 'reported' ? c.surface1 : c.surface2,
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 11, color: c.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Top Listening Countries
          </p>
          <span style={{ fontSize: 11, color: tone, fontWeight: 600 }}>{analyticsAvailabilityLabel(availability)}</span>
        </div>
        {rankedCountries.length === 0 ? (
          <>
            <p
              style={{
                margin: 0,
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 22,
                color: c.text2,
              }}
            >
              Unavailable
            </p>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              No country ranking is available for this overview yet.
            </p>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
              Top 5 countries by share of all-time streams.
            </p>
            <div role="list" aria-label="Top listening countries" style={{ display: 'grid', gap: 6, marginTop: 2 }}>
              {rankedCountries.map((country, index) => {
                const flag = countryFlagEmoji(country.countryCode);
                return (
                  <div
                    key={country.countryCode}
                    role="listitem"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      minWidth: 0,
                      paddingTop: index === 0 ? 6 : 0,
                      borderTop: `1px solid ${c.border}`,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      {flag ? (
                        <span aria-hidden="true" style={{ flexShrink: 0, fontSize: 15, lineHeight: 1 }}>
                          {flag}
                        </span>
                      ) : null}
                      <span
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 11.5,
                          color: c.text2,
                        }}
                      >
                        {country.countryName}
                      </span>
                    </span>
                    <span
                      style={{
                        flexShrink: 0,
                        fontFamily: 'Space Grotesk',
                        fontWeight: 600,
                        fontSize: 13,
                        color: analyticsMetricBreakdownValueColor(country.availability),
                      }}
                    >
                      {country.share}
                    </span>
                  </div>
                );
              })}
            </div>
            <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
              {analyticsEvidenceLabel(rankedCountries[0])}
            </p>
          </>
        )}
      </article>
    );
  }

  function renderAnalyticsInsightCards(items: PublisherAnalyticsInsight[], label: string) {
    if (items.length === 0) return null;
    return (
      <div
        role="list"
        aria-label={label}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 8,
        }}
      >
        {items.map((item) => (
          <article
            key={item.key}
            role="listitem"
            style={{
              border: `1px solid ${item.availability === 'unavailable' ? c.accentBorder : c.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              background: item.availability === 'unavailable' ? c.surface2 : c.surface1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
              <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                {item.title}
              </p>
              <span style={{ fontSize: 11, color: analyticsAvailabilityColor(item.availability), fontWeight: 600 }}>
                {analyticsAvailabilityLabel(item.availability)}
              </span>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 11.5, lineHeight: 1.6, color: c.text2 }}>{item.body}</p>
            <p style={{ margin: 0, fontSize: 11, lineHeight: 1.55, color: c.text2 }}>
              {analyticsEvidenceLabel(item)}
            </p>
            {item.note ? <p style={{ margin: '6px 0 0', fontSize: 11, lineHeight: 1.55, color: c.text2 }}>{item.note}</p> : null}
          </article>
        ))}
      </div>
    );
  }

  function renderAnalyticsStreams(payload: PublisherAnalyticsPayload) {
    const series = payload.streams.series.filter((point) => point.streams !== null);
    if (series.length === 0) {
      return (
        <div style={mutedEmptyStyle}>
          {payload.streams.note ?? 'No stream history is available for this release in the current window.'}
        </div>
      );
    }

    const streamValues = series.map((point) => point.streams ?? 0);
    const listenerValues = series.map((point) => point.listeners);
    const reportedListenerValues = listenerValues.filter((value): value is number => value !== null);
    const maxValue = niceAnalyticsMaximum(Math.max(...streamValues, ...reportedListenerValues, 1));
    const peakDay = series.reduce((best, point) => ((point.streams ?? 0) > (best.streams ?? 0) ? point : best), series[0]);
    const averageDailyStreams = Math.round(streamValues.reduce((sum, value) => sum + value, 0) / streamValues.length);

    let bestGrowthLabel = 'Unavailable';
    let bestGrowthNote = 'Not enough daily points were seeded to compute day-over-day growth.';
    let bestGrowth = Number.NEGATIVE_INFINITY;
    for (let index = 1; index < series.length; index += 1) {
      const previous = series[index - 1].streams;
      const current = series[index].streams;
      if (previous === null || current === null) continue;
      const growth = current - previous;
      if (growth > bestGrowth) {
        bestGrowth = growth;
        bestGrowthLabel = `${growth >= 0 ? '+' : ''}${growth.toLocaleString()} · ${compactAnalyticsDate(series[index].date)}`;
        bestGrowthNote = 'Computed from the visible stream series for the selected range.';
      }
    }

    const streamLine = analyticsChartLine(streamValues, maxValue);
    const streamArea = analyticsChartArea(streamValues, maxValue);
    const listenerLine = reportedListenerValues.some((value) => value > 0)
      ? analyticsChartLine(listenerValues, maxValue)
      : '';
    const dateTickIndexes = analyticsDateTickIndexes(series.length);
    const yTicks = Array.from({ length: 5 }, (_, index) => (maxValue / 4) * index);
    const activePointIndex = analyticsHoveredPoint !== null && analyticsHoveredPoint < series.length
      ? analyticsHoveredPoint
      : null;
    const activePoint = activePointIndex === null ? null : series[activePointIndex];
    const activeX = activePointIndex === null ? 0 : analyticsChartX(activePointIndex, series.length);
    const tooltipWidth = 160;
    const tooltipX = Math.min(
      Math.max(activeX - tooltipWidth / 2, ANALYTICS_CHART_MARGIN.left),
      ANALYTICS_CHART_WIDTH - ANALYTICS_CHART_MARGIN.right - tooltipWidth
    );

    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <section style={{ ...panelStyle, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div>
              <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                Streams over time
              </p>
              <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: c.text2 }}>
                {payload.streams.note ?? 'Daily stream totals for the selected reporting window.'}
              </p>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>
              {payload.reportedAt ? `Last reported ${formatTimestamp(payload.reportedAt)}` : 'No provider report timestamp yet'}
            </p>
          </div>

          <div className="lyx-publisher-analytics-chart-frame">
            <div className="lyx-publisher-analytics-chart-scroll">
              <svg
                className="lyx-publisher-analytics-chart"
                viewBox={`0 0 ${ANALYTICS_CHART_WIDTH} ${ANALYTICS_CHART_HEIGHT}`}
                role="img"
                aria-label="Daily streams and unique listeners line chart"
                onPointerMove={(event) => {
                  if (event.pointerType !== 'mouse') return;
                  const bounds = event.currentTarget.getBoundingClientRect();
                  const pointerX = ((event.clientX - bounds.left) / bounds.width) * ANALYTICS_CHART_WIDTH;
                  const plotWidth = ANALYTICS_CHART_WIDTH - ANALYTICS_CHART_MARGIN.left - ANALYTICS_CHART_MARGIN.right;
                  const ratio = Math.min(1, Math.max(0, (pointerX - ANALYTICS_CHART_MARGIN.left) / plotWidth));
                  setAnalyticsHoveredPoint(Math.round(ratio * (series.length - 1)));
                }}
                onPointerLeave={() => setAnalyticsHoveredPoint(null)}
              >
                <title>Daily streams and unique listeners for the selected reporting window</title>
                {yTicks.map((value) => {
                  const y = analyticsChartY(value, maxValue);
                  return (
                    <g key={value}>
                      <line
                        x1={ANALYTICS_CHART_MARGIN.left}
                        y1={y}
                        x2={ANALYTICS_CHART_WIDTH - ANALYTICS_CHART_MARGIN.right}
                        y2={y}
                        stroke={c.border}
                        strokeDasharray={value === 0 ? undefined : '3 5'}
                      />
                      <text x={ANALYTICS_CHART_MARGIN.left - 10} y={y + 4} textAnchor="end" fill={c.text2} fontSize="11">
                        {compactAnalyticsCount(value)}
                      </text>
                    </g>
                  );
                })}
                <text
                  x="14"
                  y={ANALYTICS_CHART_MARGIN.top + (ANALYTICS_CHART_HEIGHT - ANALYTICS_CHART_MARGIN.top - ANALYTICS_CHART_MARGIN.bottom) / 2}
                  transform={`rotate(-90 14 ${ANALYTICS_CHART_MARGIN.top + (ANALYTICS_CHART_HEIGHT - ANALYTICS_CHART_MARGIN.top - ANALYTICS_CHART_MARGIN.bottom) / 2})`}
                  textAnchor="middle"
                  fill={c.text2}
                  fontSize="11"
                >
                  Daily count
                </text>
                <path d={streamArea} fill={c.accentSoft} stroke="none" />
                <path d={streamLine} fill="none" stroke={c.accent} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                {listenerLine ? (
                  <path
                    d={listenerLine}
                    fill="none"
                    stroke={c.success}
                    strokeWidth="2.5"
                    strokeDasharray="7 5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                ) : null}
                {dateTickIndexes.map((index) => {
                  const x = analyticsChartX(index, series.length);
                  return (
                    <text
                      key={series[index].date}
                      x={x}
                      y={ANALYTICS_CHART_HEIGHT - 15}
                      textAnchor={index === 0 ? 'start' : index === series.length - 1 ? 'end' : 'middle'}
                      fill={c.text2}
                      fontSize="11"
                    >
                      {compactAnalyticsDate(series[index].date)}
                    </text>
                  );
                })}
                {series.map((point, index) => {
                  const x = analyticsChartX(index, series.length);
                  const streamY = analyticsChartY(point.streams ?? 0, maxValue);
                  return (
                    <g key={point.date}>
                      <circle
                        cx={x}
                        cy={streamY}
                        r="9"
                        fill="transparent"
                        aria-hidden="true"
                      />
                      {index === 0 || index === series.length - 1 || point === peakDay ? (
                        <circle cx={x} cy={streamY} r="3.5" fill={c.surface2} stroke={c.accent} strokeWidth="2.5" pointerEvents="none" />
                      ) : null}
                    </g>
                  );
                })}
                {activePoint ? (
                  <g pointerEvents="none">
                    <line
                      x1={activeX}
                      y1={ANALYTICS_CHART_MARGIN.top}
                      x2={activeX}
                      y2={ANALYTICS_CHART_HEIGHT - ANALYTICS_CHART_MARGIN.bottom}
                      stroke={c.borderStrong}
                      strokeWidth="1.5"
                    />
                    <circle cx={activeX} cy={analyticsChartY(activePoint.streams ?? 0, maxValue)} r="5" fill={c.accent} stroke={c.surface2} strokeWidth="2" />
                    {activePoint.listeners !== null ? (
                      <circle cx={activeX} cy={analyticsChartY(activePoint.listeners, maxValue)} r="4.5" fill={c.success} stroke={c.surface2} strokeWidth="2" />
                    ) : null}
                    <rect x={tooltipX} y="24" width={tooltipWidth} height="66" rx="6" fill={c.surface1} stroke={c.borderStrong} />
                    <text x={tooltipX + 10} y="42" fill={c.text1} fontSize="11" fontWeight="600">
                      {compactAnalyticsDate(activePoint.date)}
                    </text>
                    <circle cx={tooltipX + 12} cy="58" r="3" fill={c.accent} />
                    <text x={tooltipX + 21} y="62" fill={c.text2} fontSize="11">
                      Streams  {(activePoint.streams ?? 0).toLocaleString()}
                    </text>
                    <circle cx={tooltipX + 12} cy="76" r="3" fill={c.success} />
                    <text x={tooltipX + 21} y="80" fill={c.text2} fontSize="11">
                      Listeners  {activePoint.listeners === null ? 'Not reported' : activePoint.listeners.toLocaleString()}
                    </text>
                  </g>
                ) : null}
              </svg>
            </div>
          </div>

          <div className="lyx-publisher-analytics-chart-legend">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden="true" style={{ width: 18, height: 3, borderRadius: 2, background: c.accent }} />
              <strong style={{ color: c.text1 }}>Streams</strong>
            </span>
            {listenerLine ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span aria-hidden="true" style={{ width: 18, borderTop: `3px dashed ${c.success}` }} />
                <strong style={{ color: c.text1 }}>Unique listeners</strong>
              </span>
            ) : null}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
            {payload.streams.comparisonNote ?? 'No listeners-over-time comparison is available for this release yet.'}
          </p>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          {renderAnalyticsValueCard({
            key: 'peak-streaming-day',
            label: 'Peak Streaming Day',
            value: `${(peakDay.streams ?? 0).toLocaleString()} · ${compactAnalyticsDate(peakDay.date)}`,
            availability: 'reported',
            note: 'Selected from the visible daily stream points in this window.',
            documentedByLabelGrid: true,
            providerBacked: true,
          })}
          {renderAnalyticsValueCard({
            key: 'average-daily-streams',
            label: 'Average Daily Streams',
            value: averageDailyStreams.toLocaleString(),
            availability: 'reported',
            note: 'Computed from the daily series shown above.',
            documentedByLabelGrid: false,
            providerBacked: false,
          })}
          {renderAnalyticsValueCard({
            key: 'best-day-over-day-growth',
            label: 'Best Day-over-Day Growth',
            value: bestGrowthLabel,
            availability: bestGrowth === Number.NEGATIVE_INFINITY ? 'unavailable' : 'reported',
            note: bestGrowthNote,
            documentedByLabelGrid: false,
            providerBacked: false,
          })}
        </div>
      </div>
    );
  }

  function renderAnalyticsPlatforms(payload: PublisherAnalyticsPayload) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        {payload.platforms.rows.length === 0 ? (
          <div style={mutedEmptyStyle}>
            {payload.platforms.notes[0]?.body ?? 'No platform analytics were returned for this release.'}
          </div>
        ) : (
          <section style={panelStyle}>
            <div style={{ display: 'grid', gap: 8 }}>
              {payload.platforms.rows.map((row) => {
                const sharePercent = Math.max(0, Math.min(100, parsePercent(row.share)));
                return (
                  <article
                    key={row.outletKey}
                    style={{
                      border: `1px solid ${row.availability === 'reported' ? c.border : c.accentBorder}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                      background: row.availability === 'reported' ? c.surface1 : c.surface2,
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) repeat(5, minmax(90px, 1fr))', gap: 10, alignItems: 'start' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                          {row.outletName}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>{analyticsEvidenceLabel(row)}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                          Streams
                        </p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.streams}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                          Share
                        </p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.share}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                          Trend
                        </p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.trend}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                          Gross Reported
                        </p>
                        <p style={{ margin: 0, fontSize: 12.5, color: row.grossReported === 'Not reported' ? c.text2 : c.text1, fontWeight: 600 }}>
                          {row.grossReported}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                          Cadence
                        </p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.cadence}</p>
                      </div>
                    </div>
                    <div>
                      <div style={{ height: 8, borderRadius: 999, background: c.surface3, overflow: 'hidden' }}>
                        <div style={{ width: `${sharePercent}%`, height: '100%', background: c.accent }} />
                      </div>
                      {row.note ? <p style={{ margin: '6px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{row.note}</p> : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
        {renderAnalyticsInsightCards(payload.platforms.notes, 'Platform notes')}
      </div>
    );
  }

  function renderAnalyticsGeography(payload: PublisherAnalyticsPayload) {
    if (payload.geography.countries.length === 0) {
      return (
        <div style={mutedEmptyStyle}>
          {payload.geography.notes[0]?.body ?? 'No geography analytics were returned for this release.'}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <section style={panelStyle}>
          <div style={{ display: 'grid', gap: 8 }}>
            {payload.geography.countries.map((country) => {
              const sharePercent = Math.max(0, Math.min(100, parsePercent(country.share)));
              return (
                <article
                  key={country.countryCode}
                  style={{
                    border: `1px solid ${country.availability === 'reported' ? c.border : c.accentBorder}`,
                    borderRadius: 10,
                    padding: '12px 14px',
                    background: country.availability === 'reported' ? c.surface1 : c.surface2,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) repeat(3, minmax(90px, 1fr))', gap: 10, alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                        {country.countryName}
                      </p>
                      <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>{analyticsEvidenceLabel(country)}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                        Streams
                      </p>
                      <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{country.streams}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                        Share
                      </p>
                      <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{country.share}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                        Trend
                      </p>
                      <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{country.trend}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ height: 8, borderRadius: 999, background: c.surface3, overflow: 'hidden' }}>
                      <div style={{ width: `${sharePercent}%`, height: '100%', background: c.success }} />
                    </div>
                    {country.note ? <p style={{ margin: '6px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{country.note}</p> : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
        {renderAnalyticsInsightCards(payload.geography.notes, 'Geography notes')}
      </div>
    );
  }

  function renderAnalyticsPlaylists(payload: PublisherAnalyticsPayload) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          {payload.playlists.summary.map((card) => renderAnalyticsValueCard(card))}
        </div>

        {payload.playlists.rows.length === 0 ? (
          <div style={mutedEmptyStyle}>
            {payload.playlists.insights[0]?.body ?? 'No playlist analytics were returned for this release.'}
          </div>
        ) : (
          <section style={panelStyle}>
            <div style={{ display: 'grid', gap: 8 }}>
              {payload.playlists.rows.map((row) => (
                <article
                  key={`${row.platformName}:${row.playlistName}`}
                  style={{
                    border: `1px solid ${row.availability === 'reported' ? c.border : c.accentBorder}`,
                    borderRadius: 10,
                    padding: '12px 14px',
                    background: row.availability === 'reported' ? c.surface1 : c.surface2,
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.7fr) repeat(4, minmax(90px, 1fr))',
                    gap: 10,
                    alignItems: 'start',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                      {row.playlistName}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: c.text2 }}>{analyticsEvidenceLabel(row)}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                      Platform
                    </p>
                    <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.platformName}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                      Placement
                    </p>
                    <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.placement}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                      Playlist Streams
                    </p>
                    <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.playlistStreams}</p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 3px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.text2, fontWeight: 600 }}>
                      Followers
                    </p>
                    <p style={{ margin: 0, fontSize: 12.5, color: row.followers === 'Unavailable' ? c.text2 : c.text1, fontWeight: 600 }}>
                      {row.followers}
                    </p>
                  </div>
                  {row.note ? (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{row.note}</p>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        )}

        {renderAnalyticsInsightCards(payload.playlists.insights, 'Playlist insights')}
      </div>
    );
  }

  function renderAnalyticsUnsupported(payload: PublisherAnalyticsPayload) {
    if (payload.unsupported.length === 0) return null;

    const allPreviewPending = payload.unsupported.every((item) => item.availability === 'unavailable');

    return (
      <section style={{ marginTop: 12 }}>
        <SectionHeading
          as="h3"
          size="sub"
          title={allPreviewPending ? 'PREVIEW PENDING IN THIS BUILD' : 'ADDITIONAL ANALYTICS'}
          subtitle={
            allPreviewPending
              ? 'Requested analytics fields are not seeded in this build yet.'
              : 'Additional analytics signals for the selected reporting window.'
          }
          marginBottom={10}
        />
        {renderAnalyticsInsightCards(
          payload.unsupported,
          allPreviewPending ? 'Preview-pending analytics fields' : 'Additional analytics fields'
        )}
      </section>
    );
  }

  function renderAnalyticsPanel() {
    if (dashboard.allReleases.length === 0) {
      return renderPlaceholderSection(
        'Deep-dive reporting appears here once the account has releases to inspect.',
        'No releases are available to analyse yet.'
      );
    }

    const actionNote =
      analyticsLoad.data?.actionNote ??
      'Export CSV and Download Report stay disabled in this build so the UI does not claim a file was generated when none was.';

    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription('Streaming performance and reporting overview.')}

        <div style={{ display: 'grid', gap: 14, marginBottom: 12 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(220px, 1.25fr) minmax(0, 1.75fr) auto',
              gap: 12,
              alignItems: 'end',
            }}
          >
            <Select
              label="Release"
              ariaLabel="Analytics release"
              value={selectedAnalyticsReleaseId}
              onChange={setSelectedAnalyticsReleaseId}
              options={analyticsReleaseOptions}
              style={{ minWidth: 220 }}
            />
            <Select
              label="Date Range"
              ariaLabel="Analytics date range"
              value={analyticsPreset}
              onChange={(value) => setAnalyticsPreset(value as PublisherAnalyticsRangePreset)}
              options={PUBLISHER_ANALYTICS_RANGE_ITEMS.map((item) => ({ value: item.key, label: item.label }))}
              style={{ minWidth: 180 }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" disabled aria-label="Export CSV disabled">
                Export CSV
              </Btn>
              <Btn variant="secondary" disabled aria-label="Download Report disabled">
                Download Report
              </Btn>
            </div>
          </div>

          {analyticsPreset === 'custom' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(160px, 220px))', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: c.text2 }}>Start Date</span>
                <input
                  type="date"
                  aria-label="Analytics custom start date"
                  value={analyticsCustomStartDate}
                  onChange={(event) => setAnalyticsCustomStartDate(event.target.value)}
                  style={ANALYTICS_DATE_INPUT_STYLE}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: c.text2 }}>End Date</span>
                <input
                  type="date"
                  aria-label="Analytics custom end date"
                  value={analyticsCustomEndDate}
                  onChange={(event) => setAnalyticsCustomEndDate(event.target.value)}
                  style={ANALYTICS_DATE_INPUT_STYLE}
                />
              </label>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <div>
              <p style={{ margin: '0 0 3px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 15, color: c.text1 }}>
                {analyticsShowsAllReleases
                  ? 'All releases'
                  : selectedAnalyticsRelease?.title.trim() || 'Selected release'}
              </p>
              <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                {analyticsShowsAllReleases
                  ? 'Catalogue aggregate across every release in this account.'
                  : `${selectedAnalyticsRelease?.artistName.trim() || 'Unknown artist'}${selectedAnalyticsRelease ? ` · ${selectedAnalyticsRelease.releaseType}` : ''}`}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{actionNote}</p>
            </div>
          </div>
        </div>

        {analyticsPreset === 'custom' && !customAnalyticsRangeReady ? (
          <div style={mutedEmptyStyle}>Choose both custom dates to load that analytics window.</div>
        ) : analyticsLoad.status === 'loading' ? (
          <LoadSkeleton rows={4} rowHeight={86} label="Loading Publisher analytics…" />
        ) : analyticsLoad.status === 'error' ? (
          isMissingRealPublisherRoute(analyticsLoad.error) ? (
            renderMissingPublisherRouteNotice()
          ) : (
            <LoadError message={analyticsLoad.error.message} />
          )
        ) : analyticsLoad.status === 'ready' ? (
          <>
            <div style={{ ...panelStyle, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
              <div>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: c.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                  Reporting Window
                </p>
                <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontSize: 16, fontWeight: 600, color: c.text1 }}>
                  {analyticsLoad.data.selectedRange.label}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: c.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                  Source
                </p>
                <p style={{ margin: 0, fontSize: 12, color: c.text1, fontWeight: 600 }}>
                  {analyticsLoad.data.source === 'mock'
                    ? 'Preview data'
                    : analyticsLoad.data.source === 'labelgrid'
                      ? 'LabelGrid'
                      : 'Preview pending'}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: c.text2 }}>
                  {analyticsLoad.data.reportedAt
                    ? `Last reported ${formatTimestamp(analyticsLoad.data.reportedAt)}`
                    : 'No provider report timestamp yet'}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              {renderAnalyticsKeyMetricsGrid(analyticsLoad.data.keyMetrics)}
            </div>

            <TabBar
              tabs={PUBLISHER_ANALYTICS_TAB_ITEMS}
              active={activeAnalyticsTab}
              onSelect={setActiveAnalyticsTab}
              label="Analytics sections"
              marginBottom={12}
            />

            {activeAnalyticsTab === 'streams'
              ? renderAnalyticsStreams(analyticsLoad.data)
              : activeAnalyticsTab === 'platforms'
                ? renderAnalyticsPlatforms(analyticsLoad.data)
                : activeAnalyticsTab === 'geography'
                  ? renderAnalyticsGeography(analyticsLoad.data)
                  : renderAnalyticsPlaylists(analyticsLoad.data)}

            {renderAnalyticsUnsupported(analyticsLoad.data)}
          </>
        ) : (
          <div style={mutedEmptyStyle}>Analytics has not been requested yet.</div>
        )}
      </section>
    );
  }

  function renderFinancialPanel() {
    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription('Revenue reporting, payout timing, and royalty education for this Publisher account.')}

        {financialLoad.status === 'loading' ? (
          <LoadSkeleton rows={4} rowHeight={86} label="Loading Publisher financials…" />
        ) : financialLoad.status === 'error' ? (
          isMissingRealPublisherRoute(financialLoad.error) ? (
            renderMissingPublisherRouteNotice()
          ) : (
            <LoadError message={financialLoad.error.message} onRetry={() => setFinancialLoad({ status: 'idle', data: null, error: null })} />
          )
        ) : financialLoad.status === 'ready' ? (
          <div style={{ display: 'grid', gap: 14 }}>
            {renderFinancialSummaryRow(financialLoad.data.summary)}

            <div aria-hidden="true" style={{ height: 1, background: c.border }} />

            <section style={{ ...panelStyle, display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    {financialLoad.data.breakdown.title}
                  </p>
                  {financialLoad.data.breakdown.subtitle ? (
                    <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.breakdown.subtitle}</p>
                  ) : null}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p
                    style={{
                      margin: '0 0 3px',
                      fontSize: 11,
                      color: c.text2,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                    }}
                  >
                    Source
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: c.text1, fontWeight: 600 }}>
                    {financialLoad.data.source === 'mock' ? 'Preview data' : 'LabelGrid'}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: c.text2 }}>
                    {financialLoad.data.reportedAt
                      ? `Last reported ${formatTimestamp(financialLoad.data.reportedAt)}`
                      : 'No provider report timestamp yet'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                {financialLoad.data.breakdown.items.map((item) => (
                  <div
                    key={item.key}
                    style={{
                      display: 'grid',
                      gap: 5,
                      paddingTop: item.emphasis === 'total' ? 12 : 0,
                      borderTop: item.emphasis === 'total' ? `1px solid ${c.border}` : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: item.emphasis === 'total' ? 13 : 12.5,
                          color: item.emphasis === 'total' ? c.text1 : c.text2,
                          fontWeight: item.emphasis === 'total' ? 600 : 500,
                        }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontFamily: item.emphasis === 'total' ? 'Space Grotesk' : 'IBM Plex Mono',
                          fontSize: item.emphasis === 'total' ? 20 : 14,
                          fontWeight: 600,
                          color: financialBreakdownValueColor(item.emphasis),
                          textAlign: 'right',
                        }}
                      >
                        {item.value}
                      </p>
                    </div>
                    {item.note ? <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{item.note}</p> : null}
                  </div>
                ))}
              </div>

              {financialLoad.data.breakdown.ctaLabel ? (
                <div style={{ display: 'grid', gap: 4 }}>
                  {financialLoad.data.breakdown.explainer ? (
                    <button
                      type="button"
                      onClick={() => setOpenFinancialExplainer('breakdown')}
                      style={{
                        appearance: 'none',
                        border: 'none',
                        padding: 0,
                        background: 'transparent',
                        color: c.accent,
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'Space Grotesk',
                      }}
                    >
                      {financialLoad.data.breakdown.ctaLabel}
                    </button>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        color: c.text2,
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'Space Grotesk',
                      }}
                    >
                      {financialLoad.data.breakdown.ctaLabel}
                    </p>
                  )}
                  {financialLoad.data.breakdown.ctaNote ? (
                    <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.breakdown.ctaNote}</p>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: financialSectionsCollapsed.platforms ? 0 : 2,
                }}
              >
                <div>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    {financialLoad.data.platforms.title}
                  </p>
                  {financialLoad.data.platforms.subtitle ? (
                    <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.platforms.subtitle}</p>
                  ) : null}
                </div>
                <Btn
                  variant="secondary"
                  onClick={() =>
                    setFinancialSectionsCollapsed((current) => ({
                      ...current,
                      platforms: !current.platforms,
                    }))
                  }
                  aria-expanded={!financialSectionsCollapsed.platforms}
                  aria-controls="publisher-financial-platforms-panel"
                >
                  <ChevronDown
                    size={13}
                    style={{
                      transform: financialSectionsCollapsed.platforms ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                  {financialSectionsCollapsed.platforms ? 'Show details' : 'Hide details'}
                </Btn>
              </div>
              {!financialSectionsCollapsed.platforms ? (
                <div id="publisher-financial-platforms-panel" style={{ display: 'grid', gap: 10 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(90px, 1fr))',
                      gap: 10,
                      padding: '0 2px',
                    }}
                  >
                    {['Platform', 'Streams', 'Earnings', 'Avg / Stream'].map((label) => (
                      <p
                        key={label}
                        style={{
                          margin: 0,
                          fontSize: 10.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: c.text2,
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </p>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {financialLoad.data.platforms.rows.map((row) => (
                      <article
                        key={row.outletKey}
                        style={{
                          border: `1px solid ${c.border}`,
                          borderRadius: 10,
                          padding: '12px 14px',
                          background: c.surface2,
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(90px, 1fr))',
                          gap: 10,
                          alignItems: 'start',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                            {row.outletName}
                          </p>
                          {row.note ? <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{row.note}</p> : null}
                        </div>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.streams}</p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.earnings}</p>
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.averagePerStream}</p>
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: financialSectionsCollapsed.paymentHistory ? 0 : 2,
                }}
              >
                <div>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    {financialLoad.data.paymentHistory.title}
                  </p>
                  {financialLoad.data.paymentHistory.subtitle ? (
                    <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.paymentHistory.subtitle}</p>
                  ) : null}
                </div>
                <Btn
                  variant="secondary"
                  onClick={() =>
                    setFinancialSectionsCollapsed((current) => ({
                      ...current,
                      paymentHistory: !current.paymentHistory,
                    }))
                  }
                  aria-expanded={!financialSectionsCollapsed.paymentHistory}
                  aria-controls="publisher-financial-payment-history-panel"
                >
                  <ChevronDown
                    size={13}
                    style={{
                      transform: financialSectionsCollapsed.paymentHistory ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                  {financialSectionsCollapsed.paymentHistory ? 'Show details' : 'Hide details'}
                </Btn>
              </div>
              {!financialSectionsCollapsed.paymentHistory ? (
                <div id="publisher-financial-payment-history-panel" style={{ display: 'grid', gap: 10 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.35fr) repeat(5, minmax(74px, 1fr))',
                      gap: 10,
                      padding: '0 2px',
                    }}
                  >
                    {['Month', 'Gross', 'Fee', 'Net', 'Status', 'Payout Date'].map((label) => (
                      <p
                        key={label}
                        style={{
                          margin: 0,
                          fontSize: 10.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: c.text2,
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </p>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {financialLoad.data.paymentHistory.rows.map((row) => {
                      const tone = financialPayoutStatusTone(row.status);
                      return (
                        <article
                          key={row.id}
                          style={{
                            border: `1px solid ${c.border}`,
                            borderRadius: 10,
                            padding: '12px 14px',
                            background: c.surface2,
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1.35fr) repeat(5, minmax(74px, 1fr))',
                            gap: 10,
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                              {row.month}
                            </p>
                            {row.note ? <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{row.note}</p> : null}
                          </div>
                          <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.gross}</p>
                          <p style={{ margin: 0, fontSize: 12.5, color: row.fee.startsWith('-') ? c.danger : c.text1, fontWeight: 600 }}>{row.fee}</p>
                          <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.net}</p>
                          <div>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px 8px',
                                borderRadius: 999,
                                background: tone.background,
                                color: tone.color,
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {financialPayoutStatusLabel(row.status)}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>{row.payoutDate}</p>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 10 }}>
              <div>
                <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                  {financialLoad.data.education.title}
                </p>
                {financialLoad.data.education.intro ? (
                  <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.6 }}>{financialLoad.data.education.intro}</p>
                ) : null}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, color: c.text2, fontSize: 11.5, lineHeight: 1.6 }}>
                {financialLoad.data.education.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              {financialLoad.data.education.ctaLabel ? (
                <div style={{ display: 'grid', gap: 4 }}>
                  {financialLoad.data.education.explainer ? (
                    <button
                      type="button"
                      onClick={() => setOpenFinancialExplainer('education')}
                      style={{
                        appearance: 'none',
                        border: 'none',
                        padding: 0,
                        background: 'transparent',
                        color: c.accent,
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'Space Grotesk',
                      }}
                    >
                      {financialLoad.data.education.ctaLabel}
                    </button>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        color: c.text2,
                        textAlign: 'left',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'Space Grotesk',
                      }}
                    >
                      {financialLoad.data.education.ctaLabel}
                    </p>
                  )}
                  {financialLoad.data.education.ctaNote ? (
                    <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.education.ctaNote}</p>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
                <p style={{ margin: 0, fontSize: 12, color: c.text2, fontWeight: 600 }}>
                  {financialLoad.data.payoutMethod.label}:
                </p>
                <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontSize: 13, fontWeight: 600, color: c.text1 }}>
                  {settingsLoad.status === 'ready'
                    ? payoutMethodLabel(settingsLoad.data, settingsLoad.data.payoutMethod.value as PublisherPayoutMethod)
                    : financialLoad.data.payoutMethod.method}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>
                  —{' '}
                  {settingsLoad.status === 'ready'
                    ? payoutMethodDetail(settingsLoad.data, settingsLoad.data.payoutMethod.value as PublisherPayoutMethod)
                    : financialLoad.data.payoutMethod.detail}
                </p>
                <Link
                  to={financialLoad.data.payoutMethod.actionHref || paths.publisherSettings}
                  onClick={() => setActiveSection('settings')}
                  style={{
                    color: c.accent,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'Space Grotesk',
                    textDecoration: 'none',
                  }}
                >
                  {financialLoad.data.payoutMethod.actionLabel}
                </Link>
              </div>
              {financialLoad.data.payoutMethod.note ? (
                <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{financialLoad.data.payoutMethod.note}</p>
              ) : null}
            </section>
          </div>
        ) : (
          <div style={mutedEmptyStyle}>Financial reporting has not been requested yet.</div>
        )}
      </section>
    );
  }

  function renderSettingsPanel() {
    return (
      <section style={{ marginBottom: 8 }}>
        {renderSectionDescription(
          'Publisher settings now load from the backend. Payout details and distribution defaults save to this account, while tax-document upload remains unavailable in this build.'
        )}

        {settingsLoad.status === 'loading' ? (
          <LoadSkeleton rows={3} rowHeight={82} label="Loading Publisher settings…" />
        ) : settingsLoad.status === 'error' ? (
          isMissingRealPublisherRoute(settingsLoad.error) ? (
            renderMissingPublisherRouteNotice()
          ) : (
            <LoadError message={settingsLoad.error.message} onRetry={() => setSettingsLoad({ status: 'idle', data: null, error: null })} />
          )
        ) : settingsLoad.status === 'ready' ? (
          <div style={{ display: 'grid', gap: 14 }}>
            <section style={{ ...panelStyle, display: 'grid', gap: 12 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 10,
                  alignItems: 'flex-start',
                  marginBottom: settingsSectionsCollapsed.payout ? 0 : 2,
                }}
              >
                <div>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    Payout Settings
                  </p>
                  <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                    Choose how this Publisher account should receive payouts and keep the destination details current.
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginLeft: 'auto' }}>
                  <div style={{ textAlign: 'right' }}>
                    <p
                      style={{
                        margin: '0 0 3px',
                        fontSize: 11,
                        color: c.text2,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                      }}
                    >
                      Source
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: c.text1, fontWeight: 600 }}>{settingsStatusLabel}</p>
                  </div>
                  <Btn
                    variant="secondary"
                    onClick={() =>
                      setSettingsSectionsCollapsed((current) => ({
                        ...current,
                        payout: !current.payout,
                      }))
                    }
                    aria-expanded={!settingsSectionsCollapsed.payout}
                    aria-controls="publisher-settings-payout-panel"
                  >
                    <ChevronDown
                      size={13}
                      style={{
                        transform: settingsSectionsCollapsed.payout ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 160ms ease',
                      }}
                    />
                    {settingsSectionsCollapsed.payout ? 'Show details' : 'Hide details'}
                  </Btn>
                </div>
              </div>

              {!settingsSectionsCollapsed.payout ? (
                <div id="publisher-settings-payout-panel" style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'start' }}>
                    <Select
                      label={settingsLoad.data.payoutMethod.label}
                      value={settingsDraft.payoutMethod}
                      onChange={updateSettingsPayoutMethod}
                      options={settingsLoad.data.payoutMethod.options}
                    />
                    <div style={{ maxWidth: 240 }}>
                      <Select
                        label={settingsLoad.data.payoutThreshold.label}
                        value={settingsDraft.payoutThreshold}
                        onChange={updateSettingsPayoutThreshold}
                        options={settingsLoad.data.payoutThreshold.options}
                      />
                      {settingsLoad.data.payoutThreshold.note ? (
                        <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
                          {settingsLoad.data.payoutThreshold.note}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {activeSettingsPayoutFields ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <p style={{ margin: 0, fontSize: 12, color: c.text2, fontWeight: 600 }}>{activeSettingsPayoutFields.label}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'start' }}>
                        {activeSettingsPayoutFields.fields.map((field) => (
                          <div key={`${activeSettingsPayoutFields.method}:${field.key}`} style={{ display: 'grid', gap: 4 }}>
                            <TextField
                              label={field.label}
                              type={field.type === 'email' ? 'email' : 'text'}
                              value={settingsDraft.payoutDetailsByMethod[activeSettingsPayoutFields.method][field.key] ?? ''}
                              placeholder={field.placeholder ?? undefined}
                              onChange={(value) => updateSettingsPayoutField(activeSettingsPayoutFields.method, field.key, value)}
                            />
                            {field.note ? <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{field.note}</p> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 12 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginBottom: settingsSectionsCollapsed.distribution ? 0 : 2,
                }}
              >
                <div>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    {settingsLoad.data.distributionPreferences.title}
                  </p>
                  <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
                    Choose the defaults new releases should start from when they are created in this account.
                  </p>
                </div>
                <Btn
                  variant="secondary"
                  onClick={() =>
                    setSettingsSectionsCollapsed((current) => ({
                      ...current,
                      distribution: !current.distribution,
                    }))
                  }
                  aria-expanded={!settingsSectionsCollapsed.distribution}
                  aria-controls="publisher-settings-distribution-panel"
                >
                  <ChevronDown
                    size={13}
                    style={{
                      transform: settingsSectionsCollapsed.distribution ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                  {settingsSectionsCollapsed.distribution ? 'Show details' : 'Hide details'}
                </Btn>
              </div>

              {!settingsSectionsCollapsed.distribution ? (
                <div id="publisher-settings-distribution-panel" style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'start' }}>
                    <Select
                      label={settingsLoad.data.distributionPreferences.defaultGenre.label}
                      value={settingsDraft.distributionPreferences.defaultGenre}
                      onChange={(value) => updateSettingsDistributionPreference('defaultGenre', value)}
                      options={settingsLoad.data.distributionPreferences.defaultGenre.options}
                    />
                    <Select
                      label={settingsLoad.data.distributionPreferences.defaultReleaseType.label}
                      value={settingsDraft.distributionPreferences.defaultReleaseType}
                      onChange={(value) => updateSettingsDistributionPreference('defaultReleaseType', value)}
                      options={settingsLoad.data.distributionPreferences.defaultReleaseType.options}
                    />
                  </div>

                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ display: 'grid', gap: 6 }}>
                      <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>{settingsLoad.data.distributionPreferences.autoSubmitTo.label}</p>
                      <RadioCardGroup label={settingsLoad.data.distributionPreferences.autoSubmitTo.label} gap={6}>
                        {settingsLoad.data.distributionPreferences.autoSubmitTo.options.map((option) => (
                          <RadioCard
                            key={option.value}
                            name="publisher-settings-auto-submit"
                            value={option.value}
                            checked={settingsDraft.distributionPreferences.autoSubmitTo === option.value}
                            onSelect={(value) => updateSettingsDistributionPreference('autoSubmitTo', value)}
                            size="compact"
                            ariaLabel={option.label}
                          >
                            <span style={{ fontSize: 12.5, color: c.text1 }}>{option.label}</span>
                          </RadioCard>
                        ))}
                      </RadioCardGroup>
                      {settingsDraft.distributionPreferences.autoSubmitTo === 'custom_selection' ? (
                        <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
                          Custom platform routing is not editable in this build yet.
                        </p>
                      ) : null}
                    </div>

                    <div style={{ display: 'grid', gap: 6 }}>
                      <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>{settingsLoad.data.distributionPreferences.contentId.label}</p>
                      <RadioCardGroup label={settingsLoad.data.distributionPreferences.contentId.label} gap={6}>
                        {settingsLoad.data.distributionPreferences.contentId.options.map((option) => (
                          <RadioCard
                            key={option.value}
                            name="publisher-settings-content-id"
                            value={option.value}
                            checked={settingsDraft.distributionPreferences.contentId === option.value}
                            onSelect={(value) => updateSettingsDistributionPreference('contentId', value)}
                            size="compact"
                            ariaLabel={option.label}
                          >
                            <span style={{ fontSize: 12.5, color: c.text1 }}>{option.label}</span>
                          </RadioCard>
                        ))}
                      </RadioCardGroup>
                    </div>

                    <div style={{ display: 'grid', gap: 6 }}>
                      <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>{settingsLoad.data.distributionPreferences.explicitContent.label}</p>
                      <RadioCardGroup label={settingsLoad.data.distributionPreferences.explicitContent.label} gap={6}>
                        {settingsLoad.data.distributionPreferences.explicitContent.options.map((option) => (
                          <RadioCard
                            key={option.value}
                            name="publisher-settings-explicit-content"
                            value={option.value}
                            checked={settingsDraft.distributionPreferences.explicitContent === option.value}
                            onSelect={(value) => updateSettingsDistributionPreference('explicitContent', value)}
                            size="compact"
                            ariaLabel={option.label}
                          >
                            <span style={{ fontSize: 12.5, color: c.text1 }}>{option.label}</span>
                          </RadioCard>
                        ))}
                      </RadioCardGroup>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 12 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginBottom: settingsSectionsCollapsed.tax ? 0 : 2,
                }}
              >
                <div>
                  <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
                    {settingsLoad.data.taxInformation.title}
                  </p>
                </div>
                <Btn
                  variant="secondary"
                  onClick={() =>
                    setSettingsSectionsCollapsed((current) => ({
                      ...current,
                      tax: !current.tax,
                    }))
                  }
                  aria-expanded={!settingsSectionsCollapsed.tax}
                  aria-controls="publisher-settings-tax-panel"
                >
                  <ChevronDown
                    size={13}
                    style={{
                      transform: settingsSectionsCollapsed.tax ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                  {settingsSectionsCollapsed.tax ? 'Show details' : 'Hide details'}
                </Btn>
              </div>

              {!settingsSectionsCollapsed.tax ? (
                <div id="publisher-settings-tax-panel" style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gap: 0 }}>
                    {settingsLoad.data.taxInformation.items.map((item, index) => (
                      <div
                        key={item.label}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '10px 0',
                          borderTop: index === 0 ? `1px solid ${c.border}` : 'none',
                          borderBottom: `1px solid ${c.border}`,
                        }}
                      >
                        <p style={{ margin: 0, fontSize: 12.5, color: c.text1, fontWeight: 600 }}>
                          {item.label}:
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: c.text2, textAlign: 'right' }}>{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gap: 6, justifyContent: 'start' }}>
                    <Btn variant="secondary" disabled aria-label="Upload Tax Documents unavailable">
                      {settingsLoad.data.taxInformation.uploadActionLabel}
                    </Btn>
                    {settingsLoad.data.taxInformation.uploadActionNote ? (
                      <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
                        {settingsLoad.data.taxInformation.uploadActionNote}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </section>

            <section style={{ ...panelStyle, display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  {settingsSave.status === 'success' ? (
                    <p style={{ margin: 0, fontSize: 12, color: c.success, fontWeight: 600 }}>Settings saved.</p>
                  ) : settingsSave.status === 'error' ? (
                    <p style={{ margin: 0, fontSize: 12, color: c.danger, lineHeight: 1.55 }}>{settingsSave.error}</p>
                  ) : settingsDirty ? (
                    <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>You have unsaved changes.</p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>Settings are up to date.</p>
                  )}
                  {USE_MOCK_API ? (
                    <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
                      These save controls require a configured backend in runtime environments.
                    </p>
                  ) : null}
                </div>
                <Btn onClick={() => void saveSettings()} disabled={!settingsDirty || savingSettings || settingsLoad.status !== 'ready'}>
                  {savingSettings ? 'Saving…' : 'Save Settings'}
                </Btn>
              </div>
            </section>
          </div>
        ) : (
          <div style={mutedEmptyStyle}>Publisher settings have not been requested yet.</div>
        )}
      </section>
    );
  }

  function renderOverviewPanel() {
    if (activeOverview === 'publishedSongs') {
      return renderReleaseSection({
        title: 'PUBLISHED SONGS',
        subtitle: 'All Publisher releases are listed here, including live, draft, in-progress, and needs-attention records.',
        releases: dashboard.allReleases,
        emptyMessage: 'No releases yet.',
        showDeliverySummary: true,
        showHeading: false,
      });
    }
    if (activeOverview === 'liveReleases') {
      return renderReleaseSection({
        title: 'LIVE CATALOG',
        subtitle: 'Releases with provider-backed delivery data already on record.',
        releases: dashboard.liveReleases,
        emptyMessage: 'No live releases yet.',
        showDeliverySummary: true,
        showHeading: false,
      });
    }
    if (activeOverview === 'inProgressReleases') {
      return renderReleaseSection({
        title: 'IN PROGRESS',
        subtitle: 'Releases already in review or processing at the current stage of work.',
        releases: inProgressReleases,
        emptyMessage: 'No in-progress releases.',
        showHeading: false,
      });
    }
    if (activeOverview === 'needsAttention') {
      return renderReleaseSection({
        title: 'NEEDS ATTENTION',
        subtitle: 'Releases with a recorded issue that still needs a fix or retry.',
        releases: dashboard.needsAttentionReleases,
        emptyMessage: 'No releases need attention.',
        showFailureReason: true,
        showHeading: false,
      });
    }
    return renderReleaseSection({
      title: 'DRAFTS',
      subtitle: 'Release drafts that still need metadata, review, or submission work.',
      releases: draftReleases,
      emptyMessage: 'No draft releases.',
      showHeading: false,
    });
  }

  function renderPendingDeletionSection() {
    if (pendingReleaseDeletions.length === 0) return null;

    return (
      <section
        style={{
          ...panelStyle,
          display: 'grid',
          gap: 10,
        }}
        aria-label="Scheduled dashboard removals"
      >
        <div>
          <p
            style={{
              margin: '0 0 4px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'Space Grotesk',
              color: c.text1,
            }}
          >
            Scheduled for dashboard removal
          </p>
          <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
            These releases are hidden from the dashboard for 24 hours. Undo keeps them visible here.
          </p>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          {pendingReleaseDeletions.map(({ release, pending }) => {
            const title = release.title.trim() || 'Untitled release';
            const metadata = [
              release.artistName.trim() || 'Unknown artist',
              release.releaseType,
              deleteGraceRemainingLabel(pending.purgeAfter),
            ];

            return (
              <div
                key={release.id}
                style={{
                  border: `1px solid ${c.border}`,
                  background: c.surface2,
                  borderRadius: 10,
                  padding: '12px 13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                  <p
                    style={{
                      margin: '0 0 4px',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'Space Grotesk',
                      color: c.text1,
                    }}
                  >
                    {title}
                  </p>
                  <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
                    {metadata.join(' · ')}
                  </p>
                  <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.45 }}>
                    Scheduled {formatTimestamp(pending.requestedAt)}
                  </p>
                </div>
                <Btn
                  variant="secondary"
                  onClick={() => undoReleaseDeletion(release.id)}
                  aria-label={`Undo dashboard removal for ${title}`}
                  style={{ flexShrink: 0 }}
                >
                  Undo
                </Btn>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  function renderOverviewSummaryPanel() {
    const overviewStreamCard =
      overviewAnalyticsLoad.status !== 'ready'
        ? null
        : overviewAnalyticsLoad.data.keyMetrics.find((card) => card.key === 'total-streams') ?? null;
    const pendingDeletionSection = renderPendingDeletionSection();
    const financialRouteMissing =
      financialLoad.status === 'error' && isMissingRealPublisherRoute(financialLoad.error);
    const analyticsRouteMissing =
      overviewAnalyticsLoad.status === 'error' && isMissingRealPublisherRoute(overviewAnalyticsLoad.error);

    return (
      <div style={{ display: 'grid', gap: 18 }}>
        <section style={{ marginBottom: 8 }}>
          {renderSectionDescription('Your publishing summary at a glance.')}
          {pendingDeletionSection ? <div style={{ marginBottom: 14 }}>{pendingDeletionSection}</div> : null}
          <div className="lyx-publisher-counts" role="tablist" aria-label="Overview release sections">
            {OVERVIEW_ITEMS.map(({ key, label }) => {
              const active = activeOverview === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`publisher-overview-tab-${key}`}
                  aria-label={label}
                  aria-selected={active}
                  aria-controls={`publisher-overview-panel-${key}`}
                  onClick={() => setActiveOverview(key)}
                  style={overviewCardStyle(active)}
                >
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      margin: '0 0 2px',
                      fontFamily: 'Space Grotesk',
                      color: active ? c.accent : c.text1,
                    }}
                  >
                    {dashboard.overview[key]}
                  </p>
                  <p style={{ fontSize: 11, color: active ? c.accent : c.text2, margin: 0 }}>{label}</p>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="lyx-publisher-activity-group-label"
            onClick={() => setOverviewCatalogueCollapsed((collapsed) => !collapsed)}
            aria-expanded={!overviewCatalogueCollapsed}
            aria-controls={`publisher-overview-panel-${activeOverview}`}
            style={{
              appearance: 'none',
              border: 'none',
              background: 'transparent',
              padding: 0,
              marginTop: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              width: '100%',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span>{overviewCatalogueCollapsed ? 'Expand' : 'Collapse'}</span>
            <ChevronDown
              aria-hidden="true"
              size={14}
              color={c.text2}
              style={{
                flexShrink: 0,
                transform: overviewCatalogueCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease',
              }}
            />
          </button>
          {!overviewCatalogueCollapsed ? (
            <div
              role="tabpanel"
              id={`publisher-overview-panel-${activeOverview}`}
              aria-labelledby={`publisher-overview-tab-${activeOverview}`}
              style={{ marginTop: 10 }}
            >
              {renderOverviewPanel()}
            </div>
          ) : null}
        </section>

        <div aria-hidden="true" style={{ height: 1, background: c.border }} />

        <section style={{ marginBottom: 8 }}>
          {financialLoad.status === 'error' && !financialRouteMissing ? (
            <LoadError message={financialLoad.error.message} onRetry={() => setFinancialLoad({ status: 'idle', data: null, error: null })} />
          ) : overviewAnalyticsLoad.status === 'error' && !analyticsRouteMissing ? (
            <LoadError
              message={overviewAnalyticsLoad.error.message}
              onRetry={() => setOverviewAnalyticsLoad({ status: 'idle', data: null, error: null })}
            />
          ) : financialRouteMissing || analyticsRouteMissing ? (
            renderMissingPublisherRouteNotice()
          ) : financialLoad.status !== 'ready' || overviewAnalyticsLoad.status !== 'ready' ? (
            <LoadSkeleton rows={4} rowHeight={86} label="Loading Publisher overview…" />
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {renderFinancialSummaryRow(financialLoad.data.summary)}
              <div aria-hidden="true" style={{ height: 1, background: c.border }} />
              {overviewStreamCard ? renderAnalyticsValueCard(overviewStreamCard) : <div style={mutedEmptyStyle}>No stream total is available for this overview yet.</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.45fr) minmax(260px, 0.95fr)', gap: 8, alignItems: 'start' }}>
                <PublisherGeographyMapCard countries={overviewAnalyticsLoad.data.geography.countries} />
                {renderOverviewTopCountriesCard(overviewAnalyticsLoad.data.geography.countries)}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderProfilePanel() {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {renderPublisherCreditsPanel()}
        {renderSnapshotSection()}
      </div>
    );
  }

  const activeSectionPanel = (() => {
    if (activeSection === 'overview') {
      return releases.length === 0 && pendingReleaseDeletions.length === 0 ? (
        <div style={{ padding: '12px 0 20px' }}>
          {renderSectionDescription('Your publishing summary at a glance.')}
          <div style={{ ...panelStyle, textAlign: 'center', padding: '28px 18px' }}>
            <div
              style={{
                width: 40,
                height: 40,
                margin: '0 auto 14px',
                borderRadius: 10,
                background: c.surface2,
                border: `1px solid ${c.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Radio size={17} color={c.text2} />
            </div>
            <p
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 14,
                margin: '0 0 4px',
                color: c.text1,
              }}
            >
              No releases yet
            </p>
            <p style={{ fontSize: 12, color: c.text2, margin: '0 0 18px', lineHeight: 1.55 }}>
              Start a release and it will appear here as draft work until it reaches the
              distributor.
            </p>
            <Btn onClick={() => navigate(paths.publisherNewRelease)}>
              <Plus size={14} />
              New Release
            </Btn>
          </div>
        </div>
      ) : (
        renderOverviewSummaryPanel()
      );
    }
    if (activeSection === 'activity') {
      return renderActivitySection(
        'Recent release events, including milestones, outlet updates, earnings snapshots, and issues that still need attention.',
        dashboard.activity
      );
    }
    if (activeSection === 'analytics') {
      return renderAnalyticsPanel();
    }
    if (activeSection === 'financial') {
      return renderFinancialPanel();
    }
    if (activeSection === 'profile') {
      return renderProfilePanel();
    }
    return renderSettingsPanel();
  })();

  return (
    <div>
      {userIsAdmin ? (
        <div className="lyx-publisher-modebar" data-mode={effectivePublisherMode} aria-label="Publisher data mode">
          <div className="lyx-publisher-modebar-inner">
            <div className="lyx-publisher-modebar-copy">
              <span>Publisher data</span>
              <strong>{effectivePublisherMode === 'mock' ? 'Mock mode' : 'Real mode'}</strong>
            </div>
            <button
              type="button"
              className="lyx-publisher-modebar-switch"
              role="switch"
              aria-checked={effectivePublisherMode === 'mock'}
              aria-label="Show Publisher mock mode"
              onClick={toggleAdminPublisherMode}
            >
              <span aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
      {/* The header is the Publisher's own rather than StudioHeader's: Publisher credits are
          tracked in the Profile tab, while the page header stays focused on release work. */}
      <div className="lyx-publisher-head">
        <div className="lyx-publisher-head-text">
          <SectionHeading
            as="h1"
            icon={<Radio size={17} color={c.accent} />}
            title="Lyrixsa AI PUBLISHER"
            subtitle="Track published releases, resume unfinished delivery work, and review the distributor facts this build can honestly report."
            marginBottom={0}
          />
        </div>
        <Btn onClick={() => navigate(paths.publisherNewRelease)} style={{ flexShrink: 0 }}>
          <Plus size={14} />
          New Release
        </Btn>
      </div>

      {firstLoad ? (
        <LoadSkeleton rows={5} label="Loading your Publisher dashboard…" />
      ) : load.status === 'error' && releases.length === 0 ? (
        isMissingRealPublisherRoute(load.error) ? (
          renderMissingPublisherRouteNotice()
        ) : (
          <LoadError message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE} onRetry={loadPublisher} />
        )
      ) : (
        <>
          <div
            role="tablist"
            aria-label="Publisher sections"
            style={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              gap: 18,
              width: '100%',
              marginBottom: 12,
            }}
          >
            {PUBLISHER_SECTION_ITEMS.map(({ key, label }) => {
              const active = activeSection === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveSection(key)}
                  style={publisherSectionHeaderStyle(active)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div>{activeSectionPanel}</div>
          {selectedActivity ? (
            <PublisherActivityDrawer
              item={selectedActivity}
              platforms={selectedActivity.release ? platformRowsFor(selectedActivity.release.id) : []}
              onClose={() => setSelectedActivity(null)}
              onOpenRelease={(releaseId) => {
                setSelectedActivity(null);
                navigate(paths.publisherRelease(releaseId));
              }}
            />
          ) : null}
          {activeFinancialExplainer ? (
            <PublisherFinancialExplainerDrawer
              explainer={activeFinancialExplainer}
              onClose={() => setOpenFinancialExplainer(null)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
